/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package i18n.translations

import i18n.StringKey

/**
 * English translations for the application.
 */
val englishStrings: Map<StringKey, String> = mapOf(
    // App name and branding
    StringKey.APP_NAME to "wiz\u2261r",
    StringKey.APP_TAGLINE to "One number. Infinite possibilities.",

    // Navigation
    StringKey.NAV_DASHBOARD to "Dashboard",
    StringKey.NAV_TRANSACTIONS to "Transactions",
    StringKey.NAV_GOALS to "Goals",
    StringKey.NAV_BUDGET to "Budget",
    StringKey.NAV_SETTINGS to "Settings",
    StringKey.NAV_BACK to "Back",
    StringKey.NAV_HOME to "Home",

    // Authentication
    StringKey.AUTH_LOGIN to "Login",
    StringKey.AUTH_LOGOUT to "Logout",
    StringKey.AUTH_REGISTER to "Register",
    StringKey.AUTH_EMAIL to "Email Address",
    StringKey.AUTH_PASSWORD to "Password",
    StringKey.AUTH_CONFIRM_PASSWORD to "Confirm Password",
    StringKey.AUTH_FIRST_NAME to "First Name",
    StringKey.AUTH_LAST_NAME to "Last Name",
    StringKey.AUTH_POSTAL_CODE to "Postal Code",
    StringKey.AUTH_PHONE to "Phone Number (optional)",
    StringKey.AUTH_DATE_OF_BIRTH to "Date of Birth (optional)",
    StringKey.AUTH_CREATE_ACCOUNT to "Create Account",
    StringKey.AUTH_TRY_DEMO to "Try it — no account needed",
    StringKey.DEMO_BANNER to "Read-only demo — your changes won't be saved",
    StringKey.AUTH_SIGN_IN to "Sign In",
    StringKey.AUTH_FORGOT_PASSWORD to "Forgot password?",
    StringKey.AUTH_ALREADY_HAVE_ACCOUNT to "Already have an account?",
    StringKey.AUTH_NO_ACCOUNT to "Don't have an account?",
    StringKey.AUTH_CREATING_ACCOUNT to "Creating account...",
    StringKey.AUTH_SIGNING_IN to "Signing in...",
    StringKey.AUTH_PASSWORD_REQUIREMENTS to "Password requirements",
    StringKey.AUTH_PASSWORD_MIN_LENGTH to "At least 8 characters",
    StringKey.AUTH_PASSWORD_UPPERCASE to "One uppercase letter",
    StringKey.AUTH_PASSWORD_LOWERCASE to "One lowercase letter",
    StringKey.AUTH_PASSWORD_NUMBER to "One number",
    StringKey.AUTH_PASSWORDS_MATCH to "Passwords match",
    StringKey.AUTH_PASSWORDS_DONT_MATCH to "Passwords do not match",
    StringKey.AUTH_MFA_TITLE to "Two-Factor Authentication",
    StringKey.AUTH_MFA_DESCRIPTION to "Enter the 6-digit code from your authenticator app",
    StringKey.AUTH_MFA_CODE to "Verification Code",
    StringKey.AUTH_MFA_VERIFY to "Verify",
    StringKey.AUTH_MFA_VERIFYING to "Verifying...",
    StringKey.AUTH_CHECK_EMAIL_TITLE to "Check Your Email",
    StringKey.AUTH_CHECK_EMAIL_SENT to "We sent a verification link to",
    StringKey.AUTH_CHECK_EMAIL_INSTRUCTIONS to "Click the link in the email to verify your account and get started.",
    StringKey.AUTH_CHECK_EMAIL_GO_TO_LOGIN to "Go to Login",
    StringKey.AUTH_CHECK_EMAIL_RESEND to "Resend email",
    StringKey.AUTH_CHECK_EMAIL_SENT_SUCCESS to "Email sent!",

    // Landing page
    StringKey.LANDING_HERO_TITLE to "One Number.",
    StringKey.LANDING_HERO_SUBTITLE to "Your savings percentage is the single most powerful number in personal finance. wiz\u2261r helps you see it, understand it, and improve it\u2014until saving becomes automatic.",
    StringKey.LANDING_HERO_SUBTITLE_SHORT to "Forget complicated budgets. Focus on one metric. Watch your wealth grow.",
    StringKey.LANDING_SAVINGS_RATE to "savings rate",
    StringKey.LANDING_ONE_NUMBER to "One Number.",
    StringKey.LANDING_INFINITE_POSSIBILITIES to "It all comes back to it.",
    StringKey.LANDING_GET_STARTED to "Get Started",
    StringKey.LANDING_SEE_HOW_IT_WORKS to "See How It Works",
    StringKey.LANDING_DISCOVER_YOUR_NUMBER to "Discover Your Number",
    StringKey.LANDING_FIND_MY_SAVINGS_RATE to "Find My Savings Rate",
    StringKey.LANDING_BANK_LEVEL_SECURITY to "Bank-level security",
    StringKey.LANDING_INSTITUTIONS_COUNT to "12,000+ institutions",
    StringKey.LANDING_NO_CREDIT_CARD to "No credit card required",
    StringKey.LANDING_WHY_ONE_NUMBER to "Why One Number Changes Everything",
    StringKey.LANDING_WHY_ONE_NUMBER_DESC to "Most finance apps drown you in data\u2014dozens of budget categories, endless charts, overwhelming dashboards. wiz\u2261r takes a different approach. A <em style=\"font-style: italic;\">wiser</em> approach. Focus on one number, and everything else falls into place.",
    StringKey.LANDING_SIMPLE_MATH to "Simple Approach, Powerful Results",
    StringKey.LANDING_SIMPLE_MATH_DESC to "Your savings rate only grows when you assign money to goals. No noise from daily expenses\u2014just intentional progress you control.",
    StringKey.LANDING_PSYCHOLOGY_TITLE to "The Psychology of One Metric",
    StringKey.LANDING_PSYCHOLOGY_DESC to "Research shows we're terrible at tracking multiple metrics. But give someone ONE number to improve? That's when habits form. That's when change sticks.",
    StringKey.LANDING_PSYCHOLOGY_STAT to "3x more likely to achieve goals",
    StringKey.LANDING_SMALL_CHANGES to "Small Changes, Massive Impact",
    StringKey.LANDING_SMALL_CHANGES_DESC to "Improving your savings rate by just 1% per month sounds small. But over 10 years? That's the difference between struggling and financial freedom.",
    StringKey.LANDING_EVERYTHING_YOU_NEED to "Everything You Need to Improve Your Number",
    StringKey.LANDING_SEE_REAL_NUMBER to "See Your Real Number",
    StringKey.LANDING_SEE_REAL_NUMBER_DESC to "Connect your accounts and instantly see your true savings percentage\u2014not a guess, not an estimate, but your actual number calculated from real transactions.",
    StringKey.LANDING_UPDATED_REALTIME to "Updated in real-time",
    StringKey.LANDING_UNDERSTAND_WHAT_MOVES to "Understand What Moves It",
    StringKey.LANDING_UNDERSTAND_DESC to "Our AI categorizes every transaction and shows you exactly which spending habits are affecting your percentage. No judgment\u2014just clarity.",
    StringKey.LANDING_AI_POWERED to "AI-powered insights",
    StringKey.LANDING_IMPROVE_AUTOMATICALLY to "Improve It Automatically",
    StringKey.LANDING_IMPROVE_DESC to "Get daily advice cards tailored to YOUR spending patterns. Small, actionable tips that compound into big improvements.",
    StringKey.LANDING_PERSONALIZED to "Personalized recommendations",
    StringKey.LANDING_BUILDING_HABITS to "Building Habits That Last",
    StringKey.LANDING_BUILDING_HABITS_DESC to "Knowing your number is step one. Improving it requires building new habits. That's where our Advice Cards come in\u2014personalized, bite-sized insights delivered at the perfect moment.",
    StringKey.LANDING_ADVICE_FOOTER to "These aren't generic tips\u2014they're insights generated from YOUR actual spending.",
    StringKey.LANDING_PRICING_TITLE to "Simple, Transparent Pricing",
    StringKey.LANDING_PRICING_SUBTITLE to "Two plans, both with a 14-day free trial. Pick the one that fits.",
    StringKey.LANDING_PRICING_CALCULATOR to "",
    StringKey.LANDING_WHATS_YOUR_NUMBER to "What's Your Number?",
    StringKey.LANDING_WHATS_YOUR_NUMBER_DESC to "Right now, you're saving some percentage of your income. You might think you know what it is\u2014but most people are surprised when they see the real number.",
    StringKey.LANDING_WHATS_YOUR_NUMBER_SUBTEXT to "Discover yours in under 2 minutes. No credit card required.",
    StringKey.LANDING_DATA_SAFE to "Your Data Is Safe. Your Number Is Private.",
    StringKey.LANDING_PRIVACY_TITLE to "Your Data Is Safe. Your Number Is Private.",
    StringKey.LANDING_MY_DASHBOARD to "My Account",

    // Pricing
    StringKey.PRICING_MONTHLY to "Monthly",
    StringKey.PRICING_YEARLY to "Yearly",
    StringKey.PRICING_SAVE_14 to "Save 14%",
    StringKey.PRICING_EVERYTHING_YOU_NEED to "Everything You Need",
    StringKey.PRICING_START_FREE_TRIAL to "Start Your 14-Day Free Trial",
    StringKey.PRICING_CANCEL_ANYTIME to "Cancel anytime. No commitment.",
    StringKey.PRICING_FULL_HISTORY to "Full transaction history",
    StringKey.PRICING_CROSS_PLATFORM to "Cross-platform sync (iOS, Android, Web)",
    StringKey.PRICING_CATEGORY_CUSTOMIZATION to "Category verification & customization",
    StringKey.PRICING_UNLIMITED_ACCOUNTS to "Unlimited bank account connections",
    StringKey.PRICING_REALTIME_TRACKING to "Real-time savings % tracking",
    StringKey.PRICING_AI_ADVICE_CARDS to "AI-powered spending advice",
    StringKey.PRICING_UNLIMITED_GOALS to "Unlimited savings goals",
    StringKey.PRICING_GOAL_OPTIMIZATION to "Goal portfolio optimization",

    // Pro Tier
    StringKey.TIER_STANDARD to "Standard",
    StringKey.TIER_PRO to "Pro",
    StringKey.TIER_STANDARD_SUBTITLE to "Everything you need to track your finances",
    StringKey.TIER_PRO_SUBTITLE to "Power tools for serious budgeters",
    StringKey.PRICING_PRO_MONTHLY to "$12.99",
    StringKey.PRICING_PRO_YEARLY to "$132",
    StringKey.PRICING_PRO_YEARLY_QUARTERLY_NOTE to "Billed quarterly at \$33/quarter",
    StringKey.PRICING_SAVE_15 to "Save 15%",
    StringKey.PRICING_BEST_VALUE to "Best Value",
    StringKey.PRICING_EVERYTHING_IN_STANDARD to "Everything in Standard",
    StringKey.PRICING_BATCH_TRANSACTION_ACTIONS to "Batch Transaction Actions",
    StringKey.PRICING_MULTI_SELECT_BULK_EDIT to "Multi-select & Bulk Edit",
    StringKey.PRICING_MULTI_SELECT_AND_BATCH to "Multi-select & Batch Transaction Actions",
    StringKey.PRICING_TRANSACTION_NOTIFICATIONS to "Transaction Notifications (email & SMS)",
    StringKey.PRICING_SPLIT_TRANSACTIONS to "Split Transaction Support",
    StringKey.PRICING_DISTRIBUTE_TRANSACTIONS to "Distribute & Move Transactions",
    StringKey.PRICING_CUSTOMIZABLE_DASHBOARD to "Customizable Grid Dashboard",
    StringKey.PRICING_CONTEXT_FILTERS to "Filter by Contexts (Vacation, Side Business...)",
    StringKey.PRICING_MORE_PRO_COMING_SOON to "More Pro features coming soon",
    StringKey.PRICING_UPGRADE_TO_PRO to "Upgrade to Pro",
    StringKey.PRICING_UPGRADE_NOW to "Upgrade Now",
    StringKey.PRICING_UPGRADE_SUBTITLE to "Unlock powerful Pro features for your account",
    StringKey.PRO_UPGRADE_PROMPT_TITLE to "Pro Feature",
    StringKey.PRO_UPGRADE_PROMPT_MESSAGE to "This is a Pro feature. Upgrade to unlock batch actions and more.",
    StringKey.PRO_UPGRADE_PROMPT_CTA to "Upgrade to Pro",

    // Dashboard
    StringKey.DASHBOARD_TITLE to "Dashboard",
    StringKey.DASHBOARD_SAVINGS_RATE to "Savings Rate",
    StringKey.DASHBOARD_THIS_MONTH to "This Month",
    StringKey.DASHBOARD_LAST_MONTH to "Last Month",
    StringKey.DASHBOARD_INCOME to "Income",
    StringKey.DASHBOARD_EXPENSES to "Expenses",
    StringKey.DASHBOARD_SAVINGS to "Savings",
    StringKey.DASHBOARD_BUDGET to "Budget",
    StringKey.DASHBOARD_MONTHLY_SUMMARY to "Monthly Summary",
    StringKey.DASHBOARD_SPENDING_BY_CATEGORY to "Spending by Category",
    StringKey.DASHBOARD_TRENDS to "Trends",
    StringKey.DASHBOARD_PROJECTIONS to "Projections",
    StringKey.DASHBOARD_NO_DATA to "No data available",
    StringKey.DASHBOARD_CHART_VIEW to "Chart View",
    StringKey.DASHBOARD_CATEGORY_SPENDING to "Category Spending",
    StringKey.DASHBOARD_EXPENSE_DISTRIBUTION to "Expense Distribution",
    StringKey.DASHBOARD_EXPENSE_TIMELINE to "Expense Timeline",
    StringKey.DASHBOARD_SAVINGS_TREND to "Savings % Trend",
    StringKey.DASHBOARD_SAVINGS_FOR_MONTH to "savings for %s",
    StringKey.DASHBOARD_SAVINGS_SMOOTHED_INFO to "Based on your typical monthly income, smoothed across the year — so an extra-paycheque month doesn’t spike the rate.",
    StringKey.DASHBOARD_SAVINGS_RECOMMENDATION to "Savings Recommendation",
    StringKey.DASHBOARD_AVAILABLE_TO_SAVE to "You have %s available to save in %s.",
    StringKey.DASHBOARD_RECOMMENDED_ALLOCATION to "Recommended allocation:",
    StringKey.DASHBOARD_MONTHS_LEFT to "%s months left",
    StringKey.DASHBOARD_YTD_AVG_SAVINGS to "YTD: %s% avg savings",
    StringKey.DASHBOARD_TARGET to "Target",
    StringKey.DASHBOARD_ACTUAL_SAVINGS to "Actual Savings %",
    StringKey.DASHBOARD_ACCOUNTS to "Accounts",
    StringKey.DASHBOARD_SELECTED_COUNT to "%s/%s selected",
    StringKey.DASHBOARD_COMPARISON to "Comparison",
    StringKey.DASHBOARD_NEEDS_WANTS_FLOW to "Needs & Wants Flow",
    StringKey.DASHBOARD_AVERAGE_SAVINGS_FOR_YEAR to "average savings for %s",
    StringKey.DASHBOARD_SAVINGS_CURRENT_MONTH to "savings for current month",
    StringKey.DASHBOARD_MONTH_IN_PROGRESS to "in progress",
    StringKey.DASHBOARD_INCOME_EXPENSES_SAVINGS to "Income / Expenses / Savings",
    StringKey.DASHBOARD_INCOME_EXPENSES to "Income / Expenses",
    StringKey.DASHBOARD_AVAILABLE_THIS_MONTH to "You have %s available in %s.",
    StringKey.DASHBOARD_OVER_BUDGET_THIS_MONTH to "You're over budget by %s in %s.",
    StringKey.DASHBOARD_OVER_BUDGET_REDUCE to "You're over budget by %s this month. Focus on reducing expenses before allocating to goals.",
    StringKey.DASHBOARD_SAVINGS_BY_MONTH to "Savings % by Month",
    StringKey.DASHBOARD_LINK_ACCOUNTS_SAVINGS to "Link your accounts to track available savings.",
    StringKey.DASHBOARD_NUDGE_SPEND_LESS to "You're spending about as much as you earn in %s — there's no room left for your goals. Trimming even a small slice (a $50 line item, a recurring subscription you don't use) is enough to start a savings streak.",
    StringKey.DASHBOARD_VIEW_BUDGET to "Review my budget →",
    StringKey.DASHBOARD_NO_MONTH_DATA to "No transactions landed in %s, so there's nothing to base a recommendation on.",
    StringKey.DASHBOARD_EARLY_MONTH_HINT to "Your month is just getting started — recommendations will appear here once your first transactions and income roll in.",
    StringKey.DASHBOARD_EXPENSES_BEFORE_INCOME to "Auto-payments have started landing but your income hasn't arrived yet — we'll suggest how to split it across your goals once your next paycheck shows up.",
    StringKey.DASHBOARD_CREATE_GOAL to "Create a Goal →",
    StringKey.DASHBOARD_GOAL_SUGGESTION to "Consider setting up savings goals to build financial security:",
    StringKey.DASHBOARD_EMERGENCY_FUND to "Emergency Fund",
    StringKey.DASHBOARD_VACATION to "Vacation",
    StringKey.DASHBOARD_HOME_DOWN_PAYMENT to "Home Down Payment",
    StringKey.DASHBOARD_RETIREMENT to "Retirement",
    StringKey.DASHBOARD_EMERGENCY_FUND_DESC to "3-6 months of expenses for unexpected costs",
    StringKey.DASHBOARD_VACATION_DESC to "Start planning your next getaway",
    StringKey.DASHBOARD_HOME_DOWN_PAYMENT_DESC to "Build toward homeownership",
    StringKey.DASHBOARD_RETIREMENT_DESC to "It's never too early to start",
    StringKey.DASHBOARD_DEMO_BADGE to "Demo",
    StringKey.DASHBOARD_DEMO_CONNECT_TITLE to "Connect your first bank account",
    StringKey.DASHBOARD_DEMO_CONNECT_DESC to "You can view sample data in the Dashboard view. Link your bank to see your real finances.",
    StringKey.DASHBOARD_DEMO_CONNECT_CTA to "Connect an account",
    StringKey.ACTIVITY_DEMO_MESSAGE to "You can view sample data in the Dashboard view. Link your bank to see your real finances.",

    // Transactions
    StringKey.TRANSACTIONS_TITLE to "Transactions",
    StringKey.TRANSACTIONS_SEARCH to "Search transactions...",
    StringKey.TRANSACTIONS_SHOW_PENDING_HOLDS to "Show pending holds",
    StringKey.TRANSACTIONS_ALL to "All",
    StringKey.TRANSACTIONS_INCOME_ONLY to "Income",
    StringKey.TRANSACTIONS_EXPENSES_ONLY to "Expenses",
    StringKey.TRANSACTIONS_TODAY to "Today",
    StringKey.TRANSACTIONS_YESTERDAY to "Yesterday",
    StringKey.TRANSACTIONS_THIS_WEEK to "This Week",
    StringKey.TRANSACTIONS_THIS_MONTH to "This Month",
    StringKey.TRANSACTIONS_OLDER to "Older",
    StringKey.TRANSACTIONS_NO_TRANSACTIONS to "No transactions found",
    StringKey.TRANSACTIONS_LOADING to "Loading transactions...",
    StringKey.TRANSACTIONS_REFRESH to "Refresh",
    StringKey.TRANSACTIONS_CATEGORY to "Category",
    StringKey.TRANSACTIONS_AMOUNT to "Amount",
    StringKey.TRANSACTIONS_DATE to "Date",
    StringKey.TRANSACTIONS_PENDING to "Pending",
    StringKey.TRANSACTIONS_SPLIT to "Split",
    StringKey.TRANSACTIONS_DIST to "Distr",
    StringKey.TRANSACTIONS_UNSPLIT to "Unsplit",
    StringKey.TRANSACTIONS_EDIT to "Edit",
    StringKey.TRANSACTIONS_DETAILS to "Transaction Details",

    // Goals
    StringKey.GOALS_TITLE to "Savings Goals",
    StringKey.GOALS_ADD_NEW to "Add New Goal",
    StringKey.GOALS_TARGET to "Target",
    StringKey.GOALS_CURRENT to "Current",
    StringKey.GOALS_PROGRESS to "Progress",
    StringKey.GOALS_DEADLINE to "Deadline",
    StringKey.GOALS_MONTHLY_CONTRIBUTION to "Monthly Contribution",
    StringKey.GOALS_ON_TRACK to "On Track",
    StringKey.GOALS_BEHIND to "Behind",
    StringKey.GOALS_AHEAD to "Ahead of Schedule",
    StringKey.GOALS_COMPLETE to "Complete",
    StringKey.GOALS_DELETE to "Delete Goal",
    StringKey.GOALS_EDIT to "Edit Goal",
    StringKey.GOALS_SAVE to "Save",
    StringKey.GOALS_CANCEL to "Cancel",
    StringKey.GOALS_NAME to "Goal Name",
    StringKey.GOALS_DESCRIPTION to "Description",
    StringKey.GOALS_NO_GOALS to "No goals yet",
    StringKey.GOALS_CREATE_FIRST to "Create your first savings goal",
    StringKey.GOALS_YOUR_GOALS to "Your Goals",
    StringKey.GOALS_PERCENT_COMPLETE to "%s% Complete",
    StringKey.GOALS_DAYS_LEFT to "%s days left",

    // Budget
    StringKey.BUDGET_TITLE to "Budget Planning",
    StringKey.BUDGET_MONTHLY_BUDGET to "Monthly Budget",
    StringKey.BUDGET_SET_BUDGET to "Set Budget",
    StringKey.BUDGET_REMAINING to "Remaining",
    StringKey.BUDGET_SPENT to "Spent",
    StringKey.BUDGET_OVER_BUDGET to "Over Budget",
    StringKey.BUDGET_UNDER_BUDGET to "Under Budget",
    StringKey.BUDGET_NO_BUDGET to "No budget set",
    StringKey.BUDGET_CREATE_PLAN to "Create Budget Plan",
    StringKey.BUDGET_EXPORT_CSV to "Export CSV",
    StringKey.BUDGET_TOTAL_INCOME to "TOTAL INCOME",
    StringKey.BUDGET_TOTAL_EXPENSES to "TOTAL EXPENSES",
    StringKey.BUDGET_NET_SAVINGS to "NET SAVINGS",
    StringKey.BUDGET_SAVINGS_PERCENT to "SAVINGS %",

    // Settings
    StringKey.SETTINGS_TITLE to "Settings",
    StringKey.SETTINGS_ACCOUNT to "Account Settings",
    StringKey.SETTINGS_SAVINGS_TARGET to "Savings Target",
    StringKey.SETTINGS_NOTIFICATIONS to "Notifications",
    StringKey.SETTINGS_PRIVACY to "Privacy",
    StringKey.SETTINGS_SECURITY to "Security",
    StringKey.SETTINGS_LANGUAGE to "Language",
    StringKey.SETTINGS_LANGUAGE_ENGLISH to "English",
    StringKey.SETTINGS_LANGUAGE_FRENCH to "Fran\u00e7ais",
    StringKey.SETTINGS_COUNTRY to "Country / Region",
    StringKey.SETTINGS_APPEARANCE to "Appearance",
    StringKey.SETTINGS_THEME_SYSTEM to "System",
    StringKey.SETTINGS_THEME_DARK to "Dark",
    StringKey.SETTINGS_THEME_LIGHT to "Light",
    StringKey.SETTINGS_MFA to "Two-Factor Authentication",
    StringKey.SETTINGS_MFA_ENABLE to "Enable MFA",
    StringKey.SETTINGS_MFA_DISABLE to "Disable MFA",
    StringKey.SETTINGS_MFA_SETUP to "Set up MFA",
    StringKey.SETTINGS_LINKED_ACCOUNTS to "Linked Accounts",
    StringKey.SETTINGS_ADD_ACCOUNT to "Add Account",
    StringKey.SETTINGS_REMOVE_ACCOUNT to "Remove Account",
    StringKey.SETTINGS_DATA_EXPORT to "Export Data",
    StringKey.SETTINGS_DELETE_ACCOUNT to "Delete Account",
    StringKey.SETTINGS_ABOUT to "About",
    StringKey.SETTINGS_VERSION to "Version",
    StringKey.SETTINGS_TERMS to "Terms of Service",
    StringKey.SETTINGS_PRIVACY_POLICY to "Privacy Policy",

    // Account linking
    StringKey.ACCOUNT_LINKING_TITLE to "Connect Your Bank Account",
    StringKey.ACCOUNT_LINKING_DESCRIPTION to "Securely connect your bank account to start tracking your transactions and managing your finances.",
    StringKey.ACCOUNT_LINKING_CONNECT to "Connect Bank Account",
    StringKey.ACCOUNT_LINKING_CONNECTING to "Connecting...",
    StringKey.ACCOUNT_LINKING_FETCHING_HISTORY to "Fetching Your Transaction History",
    StringKey.ACCOUNT_LINKING_SECURITY_ENCRYPTION to "Bank-level encryption",
    StringKey.ACCOUNT_LINKING_SECURITY_NO_CREDENTIALS to "We never store your credentials",
    StringKey.ACCOUNT_LINKING_SECURITY_READ_ONLY to "Read-only access to transactions",
    StringKey.ACCOUNT_LINKING_SECURITY_PLAID to "Powered by Plaid",
    StringKey.ACCOUNT_LINKING_PRIVACY_NOTICE to "By connecting your account, you agree to Plaid's End User Privacy Policy",
    StringKey.ACCOUNT_LINKING_COMPLETE to "complete",
    StringKey.ACCOUNT_LINKING_PROGRESS to "We're downloading your transaction history from your bank. This may take a few minutes.",
    StringKey.ACCOUNT_LINKING_SKIP to "Skip for now",
    StringKey.ACCOUNT_LINKING_DOWNLOADING to "We're downloading %s of transaction history from your bank. This may take a few minutes.",
    StringKey.ACCOUNT_LINKING_PERCENT_COMPLETE to "%s%% complete",
    StringKey.ACCOUNT_LINKING_CONNECTION_FAILED to "Connection Failed",

    // Time periods
    StringKey.PERIOD_DAY to "Day",
    StringKey.PERIOD_WEEK to "Week",
    StringKey.PERIOD_MONTH to "Month",
    StringKey.PERIOD_QUARTER to "Quarter",
    StringKey.PERIOD_YEAR to "Year",
    StringKey.PERIOD_ALL_TIME to "All Time",
    StringKey.PERIOD_3_MONTHS to "3 months",
    StringKey.PERIOD_6_MONTHS to "6 months",
    StringKey.PERIOD_12_MONTHS to "12 months",
    StringKey.PERIOD_24_MONTHS to "24 months",

    // Month names
    StringKey.MONTH_JAN to "Jan",
    StringKey.MONTH_FEB to "Feb",
    StringKey.MONTH_MAR to "Mar",
    StringKey.MONTH_APR to "Apr",
    StringKey.MONTH_MAY to "May",
    StringKey.MONTH_JUN to "Jun",
    StringKey.MONTH_JUL to "Jul",
    StringKey.MONTH_AUG to "Aug",
    StringKey.MONTH_SEP to "Sep",
    StringKey.MONTH_OCT to "Oct",
    StringKey.MONTH_NOV to "Nov",
    StringKey.MONTH_DEC to "Dec",
    StringKey.MONTH_JANUARY to "January",
    StringKey.MONTH_FEBRUARY to "February",
    StringKey.MONTH_MARCH to "March",
    StringKey.MONTH_APRIL to "April",
    StringKey.MONTH_MAY_FULL to "May",
    StringKey.MONTH_JUNE to "June",
    StringKey.MONTH_JULY to "July",
    StringKey.MONTH_AUGUST to "August",
    StringKey.MONTH_SEPTEMBER to "September",
    StringKey.MONTH_OCTOBER to "October",
    StringKey.MONTH_NOVEMBER to "November",
    StringKey.MONTH_DECEMBER to "December",

    // Common actions
    StringKey.ACTION_SAVE to "Save",
    StringKey.ACTION_CANCEL to "Cancel",
    StringKey.ACTION_DELETE to "Delete",
    StringKey.ACTION_EDIT to "Edit",
    StringKey.ACTION_CLOSE to "Close",
    StringKey.ACTION_CONFIRM to "Confirm",
    StringKey.ACTION_SUBMIT to "Submit",
    StringKey.ACTION_RETRY to "Retry",
    StringKey.ACTION_REFRESH to "Refresh",
    StringKey.ACTION_SEARCH to "Search",
    StringKey.ACTION_FILTER to "Filter",
    StringKey.ACTION_SORT to "Sort",
    StringKey.ACTION_CLEAR to "Clear",
    StringKey.ACTION_CONTINUE to "Continue",
    StringKey.ACTION_BACK to "Back",
    StringKey.ACTION_NEXT to "Next",
    StringKey.ACTION_PREVIOUS to "Previous",
    StringKey.ACTION_DONE to "Done",

    // Common labels
    StringKey.LABEL_LOADING to "Loading...",
    StringKey.LABEL_ERROR to "Error",
    StringKey.LABEL_SUCCESS to "Success",
    StringKey.LABEL_WARNING to "Warning",
    StringKey.LABEL_INFO to "Info",
    StringKey.LABEL_YES to "Yes",
    StringKey.LABEL_NO to "No",
    StringKey.LABEL_OK to "OK",
    StringKey.LABEL_OPTIONAL to "optional",
    StringKey.LABEL_REQUIRED to "required",
    StringKey.LABEL_TOTAL to "Total",
    StringKey.LABEL_AVERAGE to "Average",
    StringKey.LABEL_MONTHLY to "Monthly",
    StringKey.LABEL_WEEKLY to "Weekly",
    StringKey.LABEL_DAILY to "Daily",
    StringKey.LABEL_YEARLY to "Yearly",

    // Error messages
    StringKey.ERROR_GENERIC to "Something went wrong. Please try again.",
    StringKey.ERROR_NETWORK to "Network error. Please check your connection.",
    StringKey.ERROR_AUTH_FAILED to "Authentication failed. Please try again.",
    StringKey.ERROR_INVALID_CREDENTIALS to "Wrong email or password.",
    StringKey.ERROR_INVALID_EMAIL to "Please enter a valid email address",
    StringKey.ERROR_INVALID_PASSWORD to "Password must be at least 8 characters with uppercase, lowercase, and a number",
    StringKey.ERROR_PASSWORDS_DONT_MATCH to "Passwords do not match",
    StringKey.ERROR_REQUIRED_FIELD to "This field is required",
    StringKey.ERROR_CONNECTION_FAILED to "Connection failed. Please try again.",
    StringKey.ERROR_SYNC_FAILED to "Sync failed. Please try again.",
    StringKey.ERROR_LOAD_FAILED to "Failed to load data. Please try again.",

    // Offline
    StringKey.OFFLINE_BANNER to "You are offline. Data may get out of sync.",
    StringKey.OFFLINE_EDIT_BLOCKED to "Cannot save changes while offline.",

    // Success messages
    StringKey.SUCCESS_SAVED to "Saved successfully",
    StringKey.SUCCESS_DELETED to "Deleted successfully",
    StringKey.SUCCESS_UPDATED to "Updated successfully",
    StringKey.SUCCESS_CONNECTED to "Connected successfully",
    StringKey.SUCCESS_LOGGED_IN to "Logged in successfully",
    StringKey.SUCCESS_REGISTERED to "Account created successfully",

    // Footer
    StringKey.FOOTER_COPYRIGHT to "\u00a9 2025 wiz\u2261r Inc. All rights reserved.",
    StringKey.FOOTER_MADE_WITH_LOVE to "Made with \u2665 for your financial future",
    StringKey.FOOTER_PRODUCT to "Product",
    StringKey.FOOTER_COMPANY to "Company",
    StringKey.FOOTER_LEGAL to "Legal",
    StringKey.FOOTER_CONNECT to "Connect",
    StringKey.FOOTER_FEATURES to "Features",
    StringKey.FOOTER_PRICING to "Pricing",
    StringKey.FOOTER_SECURITY to "Security",
    StringKey.FOOTER_FAQ to "FAQ",
    StringKey.FOOTER_ABOUT to "About",
    StringKey.FOOTER_BLOG to "Blog",
    StringKey.FOOTER_CAREERS to "Careers",
    StringKey.FOOTER_CONTACT to "Contact",
    StringKey.FOOTER_TERMS to "Terms",
    StringKey.FOOTER_PRIVACY to "Privacy",
    StringKey.FOOTER_COOKIES to "Cookie Policy",

    // Advice cards
    StringKey.ADVICE_DINING_ALERT to "Dining Out Alert",
    StringKey.ADVICE_NEW_PERSONAL_BEST to "New Personal Best!",
    StringKey.ADVICE_WEEKEND_TIP to "Weekend Savings Tip",
    StringKey.ADVICE_GOAL_UPDATE to "Goal Update",

    // Chart labels
    StringKey.CHART_INCOME to "Income",
    StringKey.CHART_EXPENSES to "Expenses",

    StringKey.CHART_BUDGET to "Budget",
    StringKey.CHART_FORECAST to "Forecast",
    StringKey.CHART_ACTUAL to "Actual",

    // Formula section
    StringKey.FORMULA_YOUR_SAVINGS_PERCENT to "Your savings %",
    StringKey.FORMULA_INCOME_MINUS_EXPENSES to "Goal Savings",

    // Advice card content
    StringKey.ADVICE_DINING_SPENT to "You've spent %s on dining this month—that's %s of your income.",
    StringKey.ADVICE_DINING_BOOST to "Cutting this by half would boost your savings rate by %s.",
    StringKey.ADVICE_SEE_DINING to "See dining transactions",
    StringKey.ADVICE_PERSONAL_BEST_MESSAGE to "Your available money reached %s this month—up from %s last month!",
    StringKey.ADVICE_KEEP_IT_UP to "Keep this up and you'll have an extra %s available this year.",
    StringKey.ADVICE_SHARE_WIN to "Share your win",
    StringKey.ADVICE_WEEKEND_ACCOUNT to "Weekends account for %s of your discretionary spending.",
    StringKey.ADVICE_WEEKEND_ACTIVITY to "Planning one free activity this weekend could save you",
    StringKey.ADVICE_BROWSE_EVENTS to "Browse free local events",
    StringKey.ADVICE_VACATION_FUND to "Vacation Fund Update",
    StringKey.ADVICE_VACATION_MESSAGE to "At your current %s savings rate, you'll reach your %s goal in %s.",
    StringKey.ADVICE_VACATION_BOOST to "Boost to %s and get there in %s!",
    StringKey.ADVICE_ADJUST_ALLOCATION to "Adjust savings allocation",

    // Security badges
    StringKey.SECURITY_BANK_ENCRYPTION to "Bank-Level Encryption",
    StringKey.SECURITY_256_BIT to "256-bit AES encryption",
    StringKey.SECURITY_PLAID_PARTNERSHIP to "Plaid Partnership",
    StringKey.SECURITY_TRUSTED_APPS to "Trusted by thousands of apps",
    StringKey.SECURITY_READ_ONLY to "Read-Only Access",
    StringKey.SECURITY_NO_MOVE_MONEY to "We can never move your money",
    StringKey.SECURITY_NO_CREDENTIAL to "No Credential Storage",
    StringKey.SECURITY_LOGIN_STAYS to "Your login stays with your bank",

    // Additional footer
    StringKey.FOOTER_CCPA_NOTICE to "CCPA Notice",
    StringKey.FOOTER_X to "X",
    StringKey.FOOTER_INSTAGRAM to "Instagram",
    StringKey.FOOTER_LINKEDIN to "LinkedIn",
    StringKey.FOOTER_EMAIL to "Email",

    // Last updated
    StringKey.LABEL_LAST_UPDATED to "Last updated",

    // Primary Categories
    StringKey.CATEGORY_INCOME to "Income",
    StringKey.CATEGORY_TRANSFER_IN to "Transfer In",
    StringKey.CATEGORY_TRANSFER_OUT to "Transfer Out",
    StringKey.CATEGORY_LOAN_PAYMENTS to "Loan Payments",
    StringKey.CATEGORY_BANK_FEES to "Bank Fees",
    StringKey.CATEGORY_ENTERTAINMENT to "Entertainment",
    StringKey.CATEGORY_FOOD_AND_DRINK to "Food & Drink",
    StringKey.CATEGORY_GENERAL_MERCHANDISE to "General Merchandise",
    StringKey.CATEGORY_HOME_IMPROVEMENT to "Home Improvement",
    StringKey.CATEGORY_MEDICAL to "Medical",
    StringKey.CATEGORY_PERSONAL_CARE to "Personal Care",
    StringKey.CATEGORY_GENERAL_SERVICES to "General Services",
    StringKey.CATEGORY_GOVERNMENT_AND_NON_PROFIT to "Government & Non-Profit",
    StringKey.CATEGORY_TRANSPORTATION to "Transportation",
    StringKey.CATEGORY_TRAVEL to "Travel",
    StringKey.CATEGORY_RENT_AND_UTILITIES to "Rent & Utilities",
    StringKey.CATEGORY_OTHER to "Other",
    StringKey.CATEGORY_MISCELLANEOUS to "Miscellaneous",
    StringKey.CATEGORY_FAMILY to "Family",
    StringKey.CATEGORY_FAMILY_CHILD_SUPPORT to "Child Support",
    StringKey.CATEGORY_FAMILY_SPOUSAL_SUPPORT to "Spousal Support",
    StringKey.CATEGORY_FAMILY_ELDER_CARE to "Elder Care",
    StringKey.CATEGORY_FAMILY_OTHER to "Other Family Support",

    // Sub-categories: Income
    StringKey.CATEGORY_INCOME_DIVIDENDS to "Dividends",
    StringKey.CATEGORY_INCOME_INTEREST_EARNED to "Interest Earned",
    StringKey.CATEGORY_INCOME_RETIREMENT_PENSION to "Retirement Pension",
    StringKey.CATEGORY_INCOME_TAX_REFUND to "Tax Refund",
    StringKey.CATEGORY_INCOME_UNEMPLOYMENT to "Unemployment",
    StringKey.CATEGORY_INCOME_WAGES to "Wages",
    StringKey.CATEGORY_INCOME_OTHER to "Other Income",

    // Sub-categories: Transfer In
    StringKey.CATEGORY_TRANSFER_IN_CASH_ADVANCES to "Cash Advances and Loans",
    StringKey.CATEGORY_TRANSFER_IN_DEPOSIT to "Deposit",
    StringKey.CATEGORY_TRANSFER_IN_INVESTMENT to "Investment and Retirement Funds",
    StringKey.CATEGORY_TRANSFER_IN_SAVINGS to "Savings",
    StringKey.CATEGORY_TRANSFER_IN_ACCOUNT to "Account Transfer",
    StringKey.CATEGORY_TRANSFER_IN_OTHER to "Other Transfer In",

    // Sub-categories: Transfer Out
    StringKey.CATEGORY_TRANSFER_OUT_INVESTMENT to "Investment and Retirement Funds",
    StringKey.CATEGORY_TRANSFER_OUT_SAVINGS to "Savings",
    StringKey.CATEGORY_TRANSFER_OUT_WITHDRAWAL to "Withdrawal",
    StringKey.CATEGORY_TRANSFER_OUT_ACCOUNT to "Account Transfer",
    StringKey.CATEGORY_TRANSFER_OUT_OTHER to "Other Transfer Out",

    // Sub-categories: Loan Payments
    StringKey.CATEGORY_LOAN_CAR to "Car Payment",
    StringKey.CATEGORY_LOAN_CREDIT_CARD to "Credit Card Payment",
    StringKey.CATEGORY_LOAN_PERSONAL to "Personal Loan Payment",
    StringKey.CATEGORY_LOAN_MORTGAGE to "Mortgage Payment",
    StringKey.CATEGORY_LOAN_STUDENT to "Student Loan Payment",
    StringKey.CATEGORY_LOAN_OTHER to "Other Payment",

    // Sub-categories: Bank Fees
    StringKey.CATEGORY_BANK_ATM to "ATM Fees",
    StringKey.CATEGORY_BANK_FOREIGN_TRANSACTION to "Foreign Transaction Fees",
    StringKey.CATEGORY_BANK_INSUFFICIENT_FUNDS to "Insufficient Funds",
    StringKey.CATEGORY_BANK_INTEREST_CHARGE to "Interest Charge",
    StringKey.CATEGORY_BANK_OVERDRAFT to "Overdraft Fees",
    StringKey.CATEGORY_BANK_OTHER to "Other Bank Fees",

    // Sub-categories: Entertainment
    StringKey.CATEGORY_ENTERTAINMENT_GAMBLING to "Casinos and Gambling",
    StringKey.CATEGORY_ENTERTAINMENT_MUSIC to "Music and Audio",
    StringKey.CATEGORY_ENTERTAINMENT_EVENTS to "Sporting Events, Amusement Parks, and Museums",
    StringKey.CATEGORY_ENTERTAINMENT_TV_MOVIES to "TV and Movies",
    StringKey.CATEGORY_ENTERTAINMENT_VIDEO_GAMES to "Video Games",
    StringKey.CATEGORY_ENTERTAINMENT_OTHER to "Other Entertainment",

    // Sub-categories: Food and Drink
    StringKey.CATEGORY_FOOD_ALCOHOL to "Beer, Wine, and Liquor",
    StringKey.CATEGORY_FOOD_COFFEE to "Coffee",
    StringKey.CATEGORY_FOOD_FAST_FOOD to "Fast Food",
    StringKey.CATEGORY_FOOD_GROCERIES to "Groceries",
    StringKey.CATEGORY_FOOD_RESTAURANT to "Restaurant",
    StringKey.CATEGORY_FOOD_VENDING to "Vending Machines",
    StringKey.CATEGORY_FOOD_OTHER to "Other Food and Drink",

    // Sub-categories: General Merchandise
    StringKey.CATEGORY_MERCHANDISE_BOOKS to "Bookstores and Newsstands",
    StringKey.CATEGORY_MERCHANDISE_CLOTHING to "Clothing and Accessories",
    StringKey.CATEGORY_MERCHANDISE_CONVENIENCE to "Convenience Stores",
    StringKey.CATEGORY_MERCHANDISE_DEPARTMENT to "Department Stores",
    StringKey.CATEGORY_MERCHANDISE_DISCOUNT to "Discount Stores",
    StringKey.CATEGORY_MERCHANDISE_ELECTRONICS to "Electronics",
    StringKey.CATEGORY_MERCHANDISE_GIFTS to "Gifts and Novelties",
    StringKey.CATEGORY_MERCHANDISE_OFFICE to "Office Supplies",
    StringKey.CATEGORY_MERCHANDISE_ONLINE to "Online Marketplaces",
    StringKey.CATEGORY_MERCHANDISE_PET to "Pet Supplies",
    StringKey.CATEGORY_MERCHANDISE_SPORTING to "Sporting Goods",
    StringKey.CATEGORY_MERCHANDISE_SUPERSTORES to "Superstores",
    StringKey.CATEGORY_MERCHANDISE_TOBACCO to "Tobacco and Vape",
    StringKey.CATEGORY_MERCHANDISE_OTHER to "Other General Merchandise",

    // Sub-categories: Home Improvement
    StringKey.CATEGORY_HOME_FURNITURE to "Furniture",
    StringKey.CATEGORY_HOME_HARDWARE to "Hardware",
    StringKey.CATEGORY_HOME_REPAIR to "Repair and Maintenance",
    StringKey.CATEGORY_HOME_SECURITY to "Security",
    StringKey.CATEGORY_HOME_OTHER to "Other Home Improvement",

    // Sub-categories: Medical
    StringKey.CATEGORY_MEDICAL_DENTAL to "Dental Care",
    StringKey.CATEGORY_MEDICAL_EYE to "Eye Care",
    StringKey.CATEGORY_MEDICAL_NURSING to "Nursing Care",
    StringKey.CATEGORY_MEDICAL_PHARMACY to "Pharmacies and Supplements",
    StringKey.CATEGORY_MEDICAL_PRIMARY to "Primary Care",
    StringKey.CATEGORY_MEDICAL_VETERINARY to "Veterinary Services",
    StringKey.CATEGORY_MEDICAL_OTHER to "Other Medical",

    // Sub-categories: Personal Care
    StringKey.CATEGORY_PERSONAL_GYM to "Gyms and Fitness Centers",
    StringKey.CATEGORY_PERSONAL_HAIR_BEAUTY to "Hair and Beauty",
    StringKey.CATEGORY_PERSONAL_LAUNDRY to "Laundry and Dry Cleaning",
    StringKey.CATEGORY_PERSONAL_OTHER to "Other Personal Care",

    // Sub-categories: General Services
    StringKey.CATEGORY_SERVICES_ACCOUNTING to "Accounting and Financial Planning",
    StringKey.CATEGORY_SERVICES_AUTOMOTIVE to "Automotive",
    StringKey.CATEGORY_SERVICES_CHILDCARE to "Childcare",
    StringKey.CATEGORY_SERVICES_LEGAL to "Consulting and Legal",
    StringKey.CATEGORY_SERVICES_EDUCATION to "Education",
    StringKey.CATEGORY_SERVICES_INSURANCE to "Insurance",
    StringKey.CATEGORY_SERVICES_POSTAGE to "Postage and Shipping",
    StringKey.CATEGORY_SERVICES_STORAGE to "Storage",
    StringKey.CATEGORY_SERVICES_OTHER to "Other General Services",

    // Sub-categories: Government and Non-Profit
    StringKey.CATEGORY_GOVERNMENT_DONATIONS to "Donations",
    StringKey.CATEGORY_GOVERNMENT_AGENCIES to "Government Departments and Agencies",
    StringKey.CATEGORY_GOVERNMENT_TAX to "Tax Payment",
    StringKey.CATEGORY_GOVERNMENT_OTHER to "Other Government and Non-Profit",

    // Sub-categories: Transportation
    StringKey.CATEGORY_TRANSPORT_BIKES to "Bikes and Scooters",
    StringKey.CATEGORY_TRANSPORT_GAS to "Gas Stations",
    StringKey.CATEGORY_TRANSPORT_PARKING to "Parking",
    StringKey.CATEGORY_TRANSPORT_PUBLIC to "Public Transit",
    StringKey.CATEGORY_TRANSPORT_RIDESHARE to "Taxis and Ride Shares",
    StringKey.CATEGORY_TRANSPORT_TOLLS to "Tolls",
    StringKey.CATEGORY_TRANSPORT_OTHER to "Other Transportation",

    // Sub-categories: Travel
    StringKey.CATEGORY_TRAVEL_AIRLINES to "Airlines and Aviation Services",
    StringKey.CATEGORY_TRAVEL_LODGING to "Lodging",
    StringKey.CATEGORY_TRAVEL_RENTAL_CARS to "Rental Cars",
    StringKey.CATEGORY_TRAVEL_AGENCIES to "Travel Agencies and Tour Operators",
    StringKey.CATEGORY_TRAVEL_OTHER to "Other Travel",

    // Sub-categories: Rent and Utilities
    StringKey.CATEGORY_UTILITIES_GAS_ELECTRIC to "Gas and Electricity",
    StringKey.CATEGORY_UTILITIES_INTERNET to "Internet and Cable",
    StringKey.CATEGORY_UTILITIES_RENT to "Rent",
    StringKey.CATEGORY_UTILITIES_SEWAGE to "Sewage and Waste Management",
    StringKey.CATEGORY_UTILITIES_TELEPHONE to "Telephone",
    StringKey.CATEGORY_UTILITIES_WATER to "Water",
    StringKey.CATEGORY_UTILITIES_OTHER to "Other Utilities",

    // Guide page
    StringKey.TOUR_BACK to "\u2190 Back",
    StringKey.GUIDE_TITLE to "How wiz\u2261r Works",
    StringKey.GUIDE_SUBTITLE to "Your personal savings rate tracker \u2014 automated and effortless.",
    StringKey.GUIDE_STEP1_TITLE to "Connect Your Spending Accounts",
    StringKey.GUIDE_STEP1_DESC to "Link your <strong>checking account</strong> and <strong>credit cards</strong> \u2014 the accounts where your everyday expenses happen. wiz\u2261r securely connects via <strong>Plaid</strong>, the same technology used by major banks and fintech apps. We never see or store your banking credentials.",
    StringKey.GUIDE_STEP2_TITLE to "Create Savings Goals",
    StringKey.GUIDE_STEP2_DESC to "Set up goals for the things that matter \u2014 emergency fund, vacation, house down payment, or retirement. You don't need a separate account for each goal. What matters is that the money <strong>leaves your spending account</strong> \u2014 as a transfer out to any savings, investment, or other place you don't need to track here. wiz\u2261r identifies those outgoing transfers, credits them to your goals, and keeps that money out of what's still available to spend.",
    StringKey.GUIDE_STEP3_TITLE to "Categorize Transfers as Goal Contributions",
    StringKey.GUIDE_STEP3_DESC to "When you see a transfer to a savings account in your transactions, simply assign it to one of your goals. wiz\u2261r's AI will learn your patterns and start categorizing automatically. <strong>That's it</strong> \u2014 your savings tracking is now fully automated.",
    StringKey.GUIDE_STEP4_TITLE to "Watch Your Savings Rate Grow",
    StringKey.GUIDE_STEP4_DESC to "Your dashboard shows your <strong>savings rate</strong> \u2014 the percentage of income going toward your goals. See monthly trends, get personalized recommendations, and set a target savings rate. Most financial experts recommend saving <strong>at least 20%</strong> of your income (the 50/30/20 rule).",
    StringKey.GUIDE_PRO_TIPS to "\uD83D\uDCA1 Pro Tips",
    StringKey.GUIDE_TIP_TRACK to "<strong>You don't need to track every account</strong> \u2014 just the ones where spending happens and where you're paid (checking + credit cards). As long as the money you allocate to goals leaves those accounts, the tracking is simple.",
    StringKey.GUIDE_TIP_ONE_GOAL to "<strong>One goal per savings account</strong> works best \u2014 it makes automatic tracking seamless. But it's not a must. The key is to see that the money is set aside.",
    StringKey.GUIDE_TIP_CHECK_IN to "<strong>Check in weekly</strong> \u2014 a quick glance at your dashboard keeps you motivated and catches any miscategorized transactions.",
    StringKey.GUIDE_TIP_BUDGET to "<strong>Use the budget planner</strong> to set spending limits by category and see where you can cut back to save more.",
    StringKey.GUIDE_TIP_AI to "<strong>AI gets smarter over time</strong> \u2014 the more you categorize, the more wiz≡r learns your patterns.",
    StringKey.GUIDE_CTA to "Ready to take control of your savings?",
    StringKey.GUIDE_BACK to "‹ Back",

    // Subscribe page
    StringKey.SUBSCRIBE_CHOOSE_PLAN to "Choose Your Plan",
    StringKey.SUBSCRIBE_TRIAL_SUBTITLE to "Start with a 14-day free trial. Cancel anytime.",
    StringKey.SUBSCRIBE_TRIALING_SUBTITLE to "You're on your free trial. Subscribe now to keep full access.",
    StringKey.SUBSCRIBE_MONTHLY to "Monthly",
    StringKey.SUBSCRIBE_YEARLY to "Yearly",
    StringKey.SUBSCRIBE_PER_MONTH to "/mo",
    StringKey.SUBSCRIBE_PER_YEAR to "/yr",
    StringKey.SUBSCRIBE_TRIAL_INCLUDED to "14-day free trial included",
    StringKey.SUBSCRIBE_START_FREE_TRIAL to "Start Free Trial",
    StringKey.SUBSCRIBE_BEST_VALUE to "BEST VALUE",
    StringKey.SUBSCRIBE_SAVE_PERCENT to "Save 14% \u2014 ${'$'}6.00/mo",
    StringKey.SUBSCRIBE_TRIAL_DISCLAIMER to "You won't be charged during your 14-day trial. Cancel anytime from your account settings.",
    StringKey.SUBSCRIBE_SUCCESS_TITLE to "You're all set!",
    StringKey.SUBSCRIBE_SUCCESS_DESC to "Your subscription is active. Enjoy full access to wiz≡r.",
    StringKey.SUBSCRIBE_GO_DASHBOARD to "Go to Dashboard",
    StringKey.SUBSCRIBE_CANCEL_TITLE to "No worries!",
    StringKey.SUBSCRIBE_CANCEL_DESC to "You can subscribe anytime to unlock full access.",
    StringKey.SUBSCRIBE_TRY_AGAIN to "Try Again",
    StringKey.SUBSCRIBE_GO_BACK to "Go Back",

    // Subscription banner
    StringKey.BANNER_FREE_TRIAL to "Free trial",
    StringKey.BANNER_DAYS_REMAINING to "%d days remaining",
    StringKey.BANNER_DAY_REMAINING to "1 day remaining",
    StringKey.BANNER_SUBSCRIBE_NOW to "Subscribe Now",
    StringKey.BANNER_TRIAL_ENDED to "Your trial has ended \u2014 dashboard is",
    StringKey.BANNER_READ_ONLY to "read-only",
    StringKey.BANNER_SUBSCRIBE_UNLOCK to "Subscribe to Unlock",

    // Account re-link
    StringKey.ACCOUNT_RELINK_TITLE to "Bank connection expired",
    StringKey.ACCOUNT_RELINK_DESC to "Your bank requires you to re-authenticate. Please re-link your account to resume syncing.",
    StringKey.ACCOUNT_RELINK_BUTTON to "Re-link account",

    // Reset password
    StringKey.RESET_PASSWORD_TITLE to "Reset Password",
    StringKey.RESET_PASSWORD_SUBTITLE to "Enter your email and a new password.",
    StringKey.RESET_PASSWORD_EMAIL_PLACEHOLDER to "Email address",
    StringKey.RESET_PASSWORD_NEW_PASSWORD_PLACEHOLDER to "New password (min 8 chars)",
    StringKey.RESET_PASSWORD_CONFIRM_PLACEHOLDER to "Confirm new password",
    StringKey.RESET_PASSWORD_SUBMIT to "Reset Password",
    StringKey.RESET_PASSWORD_BACK_TO_LOGIN to "\u2190 Back to Login",
    StringKey.RESET_PASSWORD_ENTER_EMAIL to "Please enter your email.",
    StringKey.RESET_PASSWORD_MIN_LENGTH to "Password must be at least 8 characters.",
    StringKey.RESET_PASSWORD_SUCCESS to "Password has been reset. You can now log in.",
    StringKey.RESET_PASSWORD_FAILED to "Failed to reset password. Please try again.",

    // Settings extras
    StringKey.SETTINGS_CHOOSE_LANGUAGE to "Choose your preferred language",
    StringKey.SETTINGS_ACCOUNT_ACTIONS to "Account Actions",
    StringKey.SETTINGS_LOGOUT_DESC to "This will disconnect your bank account and require you to sign in again.",
    StringKey.SETTINGS_DANGER_ZONE to "Danger Zone",
    StringKey.SETTINGS_DELETE_ACCOUNT_DESC to "Permanently delete your account and all associated data. This action cannot be undone.",
    StringKey.SETTINGS_DELETE_CONFIRM_TITLE to "\u26A0\uFE0F Delete Account",
    StringKey.SETTINGS_DELETE_CONFIRM_DESC to "This will permanently delete your account and all your data, including:",
    StringKey.SETTINGS_DELETE_CONFIRM_LIST_ACCOUNTS to "All linked bank accounts",
    StringKey.SETTINGS_DELETE_CONFIRM_LIST_TRANSACTIONS to "Transaction history",
    StringKey.SETTINGS_DELETE_CONFIRM_LIST_GOALS to "Savings goals and progress",
    StringKey.SETTINGS_DELETE_CONFIRM_LIST_BUDGETS to "Budget plans",
    StringKey.SETTINGS_DELETE_CONFIRM_LIST_SUBSCRIPTION to "Your subscription (if active)",
    StringKey.SETTINGS_DELETE_CANNOT_UNDO to "This action cannot be undone.",
    StringKey.SETTINGS_DELETE_FOREVER to "Delete Forever",
    StringKey.SETTINGS_DELETING to "Deleting...",
    StringKey.SETTINGS_DELETE_REAUTH_TITLE to "Confirm with your password",
    StringKey.SETTINGS_DELETE_REAUTH_MESSAGE to "Enter your current password to confirm you really want to delete your account.",
    StringKey.SETTINGS_DELETE_REAUTH_PASSWORD_PLACEHOLDER to "Current password",
    StringKey.SETTINGS_DELETE_REAUTH_EMPTY_PASSWORD to "Please enter your current password.",
    StringKey.SETTINGS_DELETE_REAUTH_INCORRECT_PASSWORD to "That password is incorrect. Try again.",
    StringKey.SETTINGS_DELETE_REAUTH_RATE_LIMITED to "Too many attempts. Please wait an hour and try again.",
    StringKey.SETTINGS_DELETE_REAUTH_GENERIC_ERROR to "Could not delete your account. Please try again.",

    // MFA extras
    StringKey.MFA_BACKUP_CODE_HINT to "You can also use a backup code",
    StringKey.MFA_BACK_TO_LOGIN to "Back to Login",
    StringKey.MFA_ENTER_VERIFICATION_CODE to "Enter verification code:",
    StringKey.MFA_VERIFICATION_CODE_LABEL to "Verification code:",
    StringKey.MFA_BACKUP_CODES_TITLE to "Backup Codes",
    StringKey.MFA_BACKUP_CODES_DESC to "Save these codes in a safe place. You can use them to log in if you lose access to your authenticator app.",
    StringKey.MFA_ENABLE to "Enable MFA",
    StringKey.MFA_DISABLE_TITLE to "Disable Two-Factor Authentication",
    StringKey.MFA_DISABLE_DESC to "Enter your current authenticator code to disable two-factor authentication.",
    StringKey.MFA_ENABLED_DESC to "Your account is protected with two-factor authentication",
    StringKey.MFA_DISABLED_DESC to "Add an extra layer of security to your account",
    StringKey.MFA_ENABLED to "Enabled",
    StringKey.MFA_DISABLED to "Disabled",
    StringKey.MFA_ENTER_CODE_ERROR to "Please enter a 6-digit code",
    StringKey.MFA_SCAN_QR to "Scan the QR code below with your authenticator app (Google Authenticator, Authy, etc.)",
    StringKey.MFA_MANUAL_CODE to "Or enter this code manually:",

    // Category display names
    StringKey.CATEGORY_DISPLAY_SHOPPING to "Shopping",
    StringKey.CATEGORY_DISPLAY_HEALTHCARE to "Healthcare",
    StringKey.CATEGORY_DISPLAY_SERVICES to "Services",
    StringKey.CATEGORY_DISPLAY_TRANSFERS_OUT to "Transfers Out",
    StringKey.CATEGORY_DISPLAY_TRANSFERS_IN to "Transfers In",
    StringKey.CATEGORY_DISPLAY_HOME to "Home",
    StringKey.CATEGORY_DISPLAY_GOVERNMENT to "Government",
    StringKey.CATEGORY_DISPLAY_RENT_MORTGAGE to "Rent/Mortgage",
    StringKey.CATEGORY_DISPLAY_UTILITIES to "Utilities",
    StringKey.CATEGORY_DISPLAY_SUBSCRIPTIONS to "Subscriptions",
    StringKey.CATEGORY_DISPLAY_INSURANCE to "Insurance",

    // Transaction detail
    StringKey.TRANSACTION_DETAILS_TITLE to "Transaction Details",
    StringKey.TRANSACTION_CATEGORY to "Category",
    StringKey.TRANSACTION_ACCOUNT to "Account",
    StringKey.TRANSACTION_DEBIT to "Debit",
    StringKey.TRANSACTION_CREDIT to "Credit",
    StringKey.TRANSACTION_GOAL to "Goal",
    StringKey.TRANSACTION_ACTIONS to "Actions",
    StringKey.TRANSACTION_CHANGE_CATEGORY to "Change Category",
    StringKey.TRANSACTION_UNSPLIT to "Unsplit Transaction",
    StringKey.TRANSACTION_UNDISTRIBUTE to "Undistribute Transaction",
    StringKey.TRANSACTION_SPLIT to "Split Transaction",
    StringKey.TRANSACTION_DISTRIBUTE to "Distribute Across Months",
    StringKey.TRANSACTION_MOVE to "Move Transaction",
    StringKey.TRANSACTION_MOVE_TO to "Move to",
    StringKey.TRANSACTION_MOVE_PREVIEW to "Move Preview",
    StringKey.TRANSACTION_MOVED_BADGE to "Moved",
    StringKey.TRANSACTION_MOVED_DESC to "This transaction has been moved to a different month. The moved item is shown in the transaction list.",
    StringKey.TRANSACTION_MOVED_TITLE to "Moved Transaction",
    StringKey.TRANSACTION_DISTRIBUTED_TITLE to "Distributed Transaction",
    StringKey.TRANSACTION_DISTRIBUTED_DESC to "This transaction has been distributed across multiple months. The distributed items are shown in the transaction list.",
    StringKey.TRANSACTION_DISTRIBUTE_TOTAL to "Total:",
    StringKey.TRANSACTION_DISTRIBUTE_START_MONTH to "Start Month",
    StringKey.TRANSACTION_DISTRIBUTE_END_MONTH to "End Month",
    StringKey.TRANSACTION_DISTRIBUTE_EACH_MONTH to "Each month",
    StringKey.TRANSACTION_DISTRIBUTE_PREVIEW to "Distribution Preview",
    StringKey.TRANSACTION_DISTRIBUTE_MONTHS to "months",
    StringKey.TRANSACTION_SPLIT_TITLE to "Split Transaction",
    StringKey.TRANSACTION_SPLIT_MANUAL_ENTRY to "Manual Entry",
    StringKey.TRANSACTION_SPLIT_CSV_IMPORT to "CSV Import",
    StringKey.TRANSACTION_SPLIT_ITEM to "Split Item",
    StringKey.TRANSACTION_SPLIT_AMOUNT to "Amount",
    StringKey.TRANSACTION_SPLIT_AMOUNT_CREDIT to "Amount (credit / refund)",
    StringKey.TRANSACTION_SPLIT_NOTE_OPTIONAL to "Note (optional)",
    StringKey.TRANSACTION_SPLIT_NOTE_PLACEHOLDER to "Add a note...",
    StringKey.TRANSACTION_SPLIT_REMOVE to "Remove",
    StringKey.TRANSACTION_SPLIT_ADD_ITEM to "+ Add Split Item",
    StringKey.TRANSACTION_SPLIT_REMAINING_WARNING to "remaining — adjust amounts to balance before saving",
    StringKey.TRANSACTION_SPLIT_OVER_WARNING to "over total — adjust amounts to balance before saving",
    StringKey.TRANSACTION_GOAL_CREATE to "Create a goal",
    StringKey.TRANSACTION_GOAL_REMAINING to "remaining",
    StringKey.TRANSACTION_NOTE_TAKE to "Take a note",
    StringKey.TRANSACTION_ADD_NOTE to "Note",
    StringKey.TRANSACTION_NOT_FOUND to "Transaction Not Found",
    StringKey.TRANSACTION_SESSION_EXPIRED to "Your session has expired. Please log in again.",

    // Misc
    StringKey.LABEL_AVAILABLE to "Available",
    StringKey.LABEL_AVAILABLE_BALANCE to "Available Balance",
    StringKey.LABEL_BANK_ACCOUNTS to "Bank Accounts",
    StringKey.LABEL_CONNECTION_ERROR to "Connection Error",
    StringKey.LABEL_LOADING_CHART to "Loading chart data...",
    StringKey.LABEL_SETTINGS to "Settings",
    StringKey.LABEL_AND_X_MORE to "... and %s more",

    // Chart titles & legends
    StringKey.CHART_SAVINGS_TREND_ROLLING to "Savings % Trend (Last 12 Months)",
    StringKey.CHART_SAVINGS_TREND_YEAR to "Savings % Trend (%s)",
    StringKey.CHART_TARGET_LABEL to "Target",
    StringKey.CHART_ACTUAL_SAVINGS_PERCENT to "Actual Savings %",
    StringKey.CHART_TARGET_PERCENT to "%s% Target",
    StringKey.CHART_CATEGORY_SPENDING_MONTH to "Category Spending (%s)",
    StringKey.CHART_NO_SPENDING_YET to "No spending this month yet",
    StringKey.CHART_LOADING_TRANSACTIONS to "Loading transaction data...",
    StringKey.CHART_EXPENSE_DIST_ROLLING to "Expense Distribution (Last 12 Months)",
    StringKey.CHART_EXPENSE_DIST_YEAR to "Expense Distribution (%s)",
    StringKey.CHART_NO_DATA_ACCOUNTS to "No data for selected accounts",
    StringKey.CHART_EXPENSE_TIMELINE_ROLLING to "Expense Timeline (Last 12 Months)",
    StringKey.CHART_EXPENSE_TIMELINE_YEAR to "Expense Timeline (%s)",
    StringKey.CHART_NO_EXPENSE_DATA to "No expense data available",
    StringKey.CHART_BUDGET_LEGEND to "Budget",
    StringKey.CHART_ACTUAL_LEGEND to "Actual",
    StringKey.CHART_OVER_BUDGET_LEGEND to "Over budget",
    StringKey.CHART_TOGGLE_BUDGET to "Budget",
    StringKey.CHART_PILL_ROLLING_12MO to "Last 12 months",
    StringKey.CHART_PILL_SELECTED_MONTH to "Selected month",
    StringKey.CHART_UNDER_FORECAST to "%s% under forecast of %s",
    StringKey.CHART_OVER_FORECAST to "over forecast of %s by %s%",
    StringKey.CHART_TARGET_TOOLTIP to "Target: %s%",

    // Dashboard recommendations
    StringKey.DASHBOARD_RECOMMENDED_ALLOCATION_HEADER to "Recommended allocation:",
    StringKey.DASHBOARD_TIME_DAYS_LEFT to "%s days",
    StringKey.DASHBOARD_TIME_MONTHS_LEFT to "%s months",

    // Goals page
    StringKey.GOALS_NO_GOALS_TITLE to "No Savings Goals Yet",
    StringKey.GOALS_NO_GOALS_DESC to "Set a savings goal and get AI-powered recommendations to help you achieve it",
    StringKey.GOALS_LOADING to "Loading goals...",
    StringKey.GOALS_PRIORITY_MUST_HAVE to "MUST HAVE",
    StringKey.GOALS_PRIORITY_NICE_TO_HAVE to "NICE TO HAVE",
    StringKey.GOALS_PRIORITY_STRETCH_GOAL to "STRETCH GOAL",
    StringKey.GOALS_PRIORITY_MAYBE to "MAYBE",
    StringKey.GOALS_PER_MONTH to "/mo",
    StringKey.GOALS_TARGET_DATE to "Target: %s",
    StringKey.GOALS_CREATED_ON to "You created this goal on %s.",

    // Goals encouragement
    StringKey.GOALS_ENCOURAGE_COMPLETE to "You did it! 🎉 Time to celebrate!",
    StringKey.GOALS_ENCOURAGE_ALMOST to "So close! Just \$%s more to go.",
    StringKey.GOALS_ENCOURAGE_HALFWAY to "Halfway there! Keep that momentum going.",
    StringKey.GOALS_ENCOURAGE_DEADLINE_SOON to "Heads up — deadline's coming fast. You need \$%s more.",
    StringKey.GOALS_ENCOURAGE_FEW_MONTHS to "A few months left. Try \$%s/month to stay on track.",
    StringKey.GOALS_ENCOURAGE_ON_TRACK to "At \$%s/month, you'll hit this goal right on time.",
    StringKey.GOALS_ENCOURAGE_EVERY_BIT to "Every little bit counts!",

    // Financial tips
    StringKey.TIPS_TITLE to "Financial Tips",
    StringKey.TIPS_SEARCH_PLACEHOLDER to "Search tips...",
    StringKey.TIPS_PINNED to "Pinned Tips",
    StringKey.TIPS_PIN_THIS to "Pin This Tip",
    StringKey.TIPS_WHY_IT_WORKS to "WHY IT WORKS",
    StringKey.TIPS_EXAMPLE to "EXAMPLE",
    StringKey.TIPS_GOT_IT to "Got it!",

    // Financial tips - Why it works
    StringKey.TIPS_WHY_50_30_20 to "This simple split removes decision fatigue. You always know exactly how much you can spend guilt-free.",
    StringKey.TIPS_WHY_AUTOMATE to "When saving requires zero willpower, it actually happens. Your future self will thank you.",
    StringKey.TIPS_WHY_24_HOUR to "Impulse buys feel urgent but rarely are. Sleeping on it reveals what you actually want vs. what caught your eye.",
    StringKey.TIPS_WHY_COMPOUND to "Interest on interest creates exponential growth. Starting early matters more than starting big.",
    StringKey.TIPS_WHY_SMALL_WINS to "Psychology shows small wins build momentum. Tiny daily habits outperform occasional big efforts.",
    StringKey.TIPS_WHY_EMERGENCY to "Financial peace comes from knowing unexpected costs won't derail you. It's insurance you pay to yourself.",
    StringKey.TIPS_WHY_TFSA to "Contributions aren't tax-deductible, but all growth and withdrawals are 100% tax-free. Great for flexible savings.",
    StringKey.TIPS_WHY_RRSP to "Contributions reduce your taxable income now. You pay tax later when you withdraw in retirement (often at a lower rate).",
    StringKey.TIPS_WHY_FHSA to "Best of both worlds: tax-deductible contributions AND tax-free withdrawals for your first home purchase.",
    StringKey.TIPS_WHY_ACCOUNTS to "Each account type has unique tax benefits. Using the right mix maximizes your long-term wealth.",
    StringKey.TIPS_WHY_DEFAULT to "Small, consistent actions compound into major results over time.",

    // Financial tips - Example labels
    StringKey.TIPS_EXAMPLE_MONTHLY_INCOME to "Monthly Income",
    StringKey.TIPS_EXAMPLE_NEEDS to "Needs (50%)",
    StringKey.TIPS_EXAMPLE_WANTS to "Wants (30%)",
    StringKey.TIPS_EXAMPLE_SAVINGS to "Savings (20%)",
    StringKey.TIPS_EXAMPLE_PAYDAY to "Payday",
    StringKey.TIPS_EXAMPLE_AUTO_TRANSFER to "Auto-transfer",
    StringKey.TIPS_EXAMPLE_YEARLY_SAVINGS to "Yearly savings",
    StringKey.TIPS_EXAMPLE_MONTHLY_CONTRIBUTION to "Monthly contribution",
    StringKey.TIPS_EXAMPLE_TIME_PERIOD to "Time period",
    StringKey.TIPS_EXAMPLE_AT_RETURN to "At 7% return",
    StringKey.TIPS_EXAMPLE_DAILY_SAVINGS to "Daily savings",
    StringKey.TIPS_EXAMPLE_WEEKLY_TOTAL to "Weekly total",
    StringKey.TIPS_EXAMPLE_YEARLY_TOTAL to "Yearly total",
    StringKey.TIPS_EXAMPLE_MONTHLY_EXPENSES to "Monthly expenses",
    StringKey.TIPS_EXAMPLE_3_MONTH_FUND to "3-month fund",
    StringKey.TIPS_EXAMPLE_6_MONTH_FUND to "6-month fund",
    StringKey.TIPS_EXAMPLE_CONTRIBUTION_ROOM to "Contribution room",
    StringKey.TIPS_EXAMPLE_WITHDRAWALS to "Withdrawals",
    StringKey.TIPS_EXAMPLE_TAX_FREE to "Tax-free",
    StringKey.TIPS_EXAMPLE_BEST_FOR to "Best for",
    StringKey.TIPS_EXAMPLE_FLEXIBLE_SAVINGS to "Flexible savings",
    StringKey.TIPS_EXAMPLE_CONTRIBUTION_LIMIT to "Contribution limit",
    StringKey.TIPS_EXAMPLE_TAX_BENEFIT to "Tax benefit",
    StringKey.TIPS_EXAMPLE_DEDUCTIBLE_NOW to "Deductible now",
    StringKey.TIPS_EXAMPLE_RETIREMENT to "Retirement",
    StringKey.TIPS_EXAMPLE_ANNUAL_LIMIT to "Annual limit",
    StringKey.TIPS_EXAMPLE_LIFETIME_MAX to "Lifetime max",
    StringKey.TIPS_EXAMPLE_FIRST_HOME to "First home",
    StringKey.TIPS_EXAMPLE_15TH_OF_MONTH to "15th of month",
    StringKey.TIPS_EXAMPLE_20_YEARS to "20 years",

    // Goal dialog
    StringKey.GOAL_DIALOG_TITLE to "New Savings Goal",
    StringKey.GOAL_DIALOG_NAME_LABEL to "Goal Name *",
    StringKey.GOAL_DIALOG_NAME_PLACEHOLDER to "e.g., Emergency Fund",
    StringKey.GOAL_DIALOG_AMOUNT_LABEL to "Target Amount *",
    StringKey.GOAL_DIALOG_DATE_LABEL to "Target Date *",
    StringKey.GOAL_DIALOG_CATEGORY_LABEL to "Category",
    StringKey.GOAL_DIALOG_PRIORITY_LABEL to "Priority",
    StringKey.GOAL_DIALOG_ICON_LABEL to "Icon",
    StringKey.GOAL_DIALOG_CREATE to "Create Goal",

    // Goal categories (dialog)
    StringKey.GOAL_CAT_EMERGENCY_FUND to "Emergency Fund",
    StringKey.GOAL_CAT_VACATION to "Vacation",
    StringKey.GOAL_CAT_HOME_PURCHASE to "Home Purchase",
    StringKey.GOAL_CAT_RETIREMENT to "Retirement",
    StringKey.GOAL_CAT_EDUCATION to "Education",
    StringKey.GOAL_CAT_MAJOR_PURCHASE to "Major Purchase",
    StringKey.GOAL_CAT_OTHER to "Other",

    // Goal priorities (dialog)
    StringKey.GOAL_PRI_MUST_HAVE to "🔴 Must Have",
    StringKey.GOAL_PRI_NICE_TO_HAVE to "🟠 Nice to Have",
    StringKey.GOAL_PRI_STRETCH_GOAL to "🔵 Stretch Goal",
    StringKey.GOAL_PRI_MAYBE to "⚪ Maybe",

    // Budget planning
    StringKey.BUDGET_COPY_FROM_YEAR to "Copy from %s",
    StringKey.BUDGET_LOGIN_TO_COPY to "Please log in to copy budget data",
    StringKey.BUDGET_COPY_SUCCESS to "Budget from %s copied to %s successfully!",
    StringKey.BUDGET_CATEGORY_CSV to "Category",

    // Recommendations page
    StringKey.RECO_TITLE to "AI Portfolio Recommendations",
    StringKey.RECO_OPTIMIZING to "Optimizing your savings plan...",
    StringKey.RECO_NO_GOALS_TITLE to "No Goals Yet",
    StringKey.RECO_NO_GOALS_DESC to "Create savings goals to get AI-powered portfolio recommendations",
    StringKey.RECO_UNABLE_TO_GENERATE to "Unable to generate allocation plan",
    StringKey.RECO_PORTFOLIO_SUMMARY to "🎯 Portfolio Summary",
    StringKey.RECO_MONTHLY_ALLOCATION to "💰 Monthly Allocation Per Goal",
    StringKey.RECO_SMART_SAVINGS to "📊 Smart Savings Plan",
    StringKey.RECO_SMART_SAVINGS_DESC to "Each reduction is automatically split across your goals based on priority and urgency",
    StringKey.RECO_TOTAL_NEEDED to "Total Needed/Month",
    StringKey.RECO_AVAILABLE_MONTH to "Available/Month",
    StringKey.RECO_OPTIMIZATION_SCORE to "Optimization Score",
    StringKey.RECO_FORECASTED_INCOME to "Forecasted Income",
    StringKey.RECO_FORECASTED_EXPENSES to "Forecasted Expenses",
    StringKey.RECO_FUNDING_GAP to "⚠️ Funding Gap Detected",
    StringKey.RECO_FUNDING_GAP_DESC to "You need \$%s more per month to fully fund all goals. High-priority goals will be funded first.",
    StringKey.RECO_PLAN_FEASIBLE to "✅ Plan is Feasible!",
    StringKey.RECO_PLAN_FEASIBLE_DESC to "You have enough savings to fund your goals. Follow the plan below to achieve them.",
    StringKey.RECO_PER_MONTH to "per month",
    StringKey.RECO_FULLY_FUNDED to "✅ Fully Funded",
    StringKey.RECO_PERCENT_OF_NEED to "%s% of need",
    StringKey.RECO_COMPLETE_IN to "Complete in:",
    StringKey.RECO_COMPLETE_NEVER to "Never",
    StringKey.RECO_COMPLETE_MONTHS to "%s months",
    StringKey.RECO_CURRENT to "Current: \$%s/mo",
    StringKey.RECO_CUT to "(%s% cut)",
    StringKey.RECO_ALLOCATION_ACROSS_GOALS to "💡 Allocation across goals:",

    // Advice card categories
    StringKey.ADVICE_CAT_SAVINGS_TIPS to "Savings Tips",
    StringKey.ADVICE_CAT_GOAL_SETTING to "Goal Setting",
    StringKey.ADVICE_CAT_ACCOUNTS to "Account Types",
    StringKey.ADVICE_CAT_ENCOURAGEMENT to "Encouragement",
    StringKey.ADVICE_CAT_BEHAVIORAL to "Behavioral Tips",

    // Advice card titles and content
    StringKey.ADVICE_TIP_50_30_20_TITLE to "The 50/30/20 Rule",
    StringKey.ADVICE_TIP_50_30_20_CONTENT to "Allocate 50% of your income to needs (rent, groceries), 30% to wants (entertainment, dining), and 20% to savings and debt repayment.",
    StringKey.ADVICE_TIP_AUTOMATE_TITLE to "Automate Your Savings",
    StringKey.ADVICE_TIP_AUTOMATE_CONTENT to "Set up automatic transfers on payday. When savings happen automatically, you're less likely to spend that money elsewhere.",
    StringKey.ADVICE_TIP_24_HOUR_TITLE to "The 24-Hour Rule",
    StringKey.ADVICE_TIP_24_HOUR_CONTENT to "Before making non-essential purchases over \$50, wait 24 hours. This cooling-off period helps you distinguish wants from needs.",
    StringKey.ADVICE_GOAL_EMERGENCY_TITLE to "Start with an Emergency Fund",
    StringKey.ADVICE_GOAL_EMERGENCY_CONTENT to "Aim to save 3-6 months of living expenses. This safety net protects you from unexpected costs and provides financial peace of mind.",
    StringKey.ADVICE_GOAL_SMART_TITLE to "SMART Goals Framework",
    StringKey.ADVICE_GOAL_SMART_CONTENT to "Make goals Specific, Measurable, Achievable, Relevant, and Time-bound. Instead of 'save more,' try 'save \$200/month for vacation by December.'",
    StringKey.ADVICE_GOAL_VISUALIZE_TITLE to "Visualize Your Success",
    StringKey.ADVICE_GOAL_VISUALIZE_CONTENT to "Name your goals after what you're saving for—'Beach Vacation 2026' is more motivating than 'Savings Goal #3.'",
    StringKey.ADVICE_ACCOUNT_TFSA_TITLE to "TFSA Basics",
    StringKey.ADVICE_ACCOUNT_TFSA_CONTENT to "Tax-Free Savings Accounts let your investments grow tax-free. Withdrawals are flexible and don't affect government benefits. Great for short-to-medium term goals.",
    StringKey.ADVICE_ACCOUNT_RRSP_TITLE to "RRSP for Retirement",
    StringKey.ADVICE_ACCOUNT_RRSP_CONTENT to "Registered Retirement Savings Plans give you a tax deduction now, but you'll pay taxes on withdrawals. Best when you expect to be in a lower tax bracket at retirement.",
    StringKey.ADVICE_ACCOUNT_FHSA_TITLE to "FHSA for First Home",
    StringKey.ADVICE_ACCOUNT_FHSA_CONTENT to "First Home Savings Accounts combine the best of TFSA and RRSP—tax deduction on contributions AND tax-free growth. Perfect for first-time homebuyers.",
    StringKey.ADVICE_ACCOUNT_401K_TITLE to "401(k) Basics",
    StringKey.ADVICE_ACCOUNT_401K_CONTENT to "A 401(k) lets you save pre-tax dollars for retirement through your employer. Many employers match contributions—that's free money. Contribute at least enough to get the full match.",
    StringKey.ADVICE_ACCOUNT_ROTH_IRA_TITLE to "Roth IRA Advantage",
    StringKey.ADVICE_ACCOUNT_ROTH_IRA_CONTENT to "Roth IRAs are funded with after-tax dollars, but all growth and withdrawals in retirement are tax-free. Great if you expect to be in a higher tax bracket later.",
    StringKey.ADVICE_ACCOUNT_TRAD_IRA_TITLE to "Traditional IRA",
    StringKey.ADVICE_ACCOUNT_TRAD_IRA_CONTENT to "Traditional IRAs give you a tax deduction on contributions now, but you'll pay taxes on withdrawals in retirement. Best when you expect to be in a lower tax bracket later.",
    StringKey.ADVICE_ACCOUNT_HSA_TITLE to "HSA: Triple Tax Benefit",
    StringKey.ADVICE_ACCOUNT_HSA_CONTENT to "Health Savings Accounts offer a triple tax advantage: tax-deductible contributions, tax-free growth, and tax-free withdrawals for medical expenses. After 65, it works like a traditional IRA.",
    StringKey.ADVICE_ENCOURAGE_COMPOUND_TITLE to "Compound Growth Magic",
    StringKey.ADVICE_ENCOURAGE_COMPOUND_CONTENT to "Saving \$200/month at 7% annual return grows to over \$120,000 in 20 years—even though you only contributed \$48,000. Time is your greatest asset!",
    StringKey.ADVICE_ENCOURAGE_SMALL_WINS_TITLE to "Small Wins Matter",
    StringKey.ADVICE_ENCOURAGE_SMALL_WINS_CONTENT to "Saving just \$5 a day adds up to \$1,825 a year. Small, consistent actions create big results over time.",
    StringKey.ADVICE_ENCOURAGE_DOING_GREAT_TITLE to "You're Doing Great!",
    StringKey.ADVICE_ENCOURAGE_DOING_GREAT_CONTENT to "Every dollar saved is a step toward financial freedom. By tracking your spending and setting goals, you're already ahead of most people.",
    StringKey.ADVICE_BEHAVIORAL_LIFESTYLE_TITLE to "Avoid Lifestyle Creep",
    StringKey.ADVICE_BEHAVIORAL_LIFESTYLE_CONTENT to "When your income increases, resist the urge to upgrade everything. Maintain your current lifestyle and direct raises toward savings.",
    StringKey.ADVICE_BEHAVIORAL_PAY_YOURSELF_TITLE to "Pay Yourself First",
    StringKey.ADVICE_BEHAVIORAL_PAY_YOURSELF_CONTENT to "Treat savings like a bill—pay it before discretionary spending. You'll adapt to living on what's left rather than saving what's left over.",
    StringKey.ADVICE_BEHAVIORAL_UNSUBSCRIBE_TITLE to "Unsubscribe from Temptation",
    StringKey.ADVICE_BEHAVIORAL_UNSUBSCRIBE_CONTENT to "Unsubscribe from retail emails and unfollow brands on social media. Reducing exposure to marketing makes it easier to stick to your budget.",

    // Goal category display names (no emoji)
    StringKey.GOAL_DISPLAY_EMERGENCY_FUND to "Emergency Fund",
    StringKey.GOAL_DISPLAY_VACATION to "Vacation",
    StringKey.GOAL_DISPLAY_HOME_PURCHASE to "Home Purchase",
    StringKey.GOAL_DISPLAY_RETIREMENT to "Retirement",
    StringKey.GOAL_DISPLAY_EDUCATION to "Education",
    StringKey.GOAL_DISPLAY_MAJOR_PURCHASE to "Major Purchase",
    StringKey.GOAL_DISPLAY_INVESTMENT to "Investment",
    StringKey.GOAL_DISPLAY_DEBT_PAYOFF to "Debt Payoff",
    StringKey.GOAL_DISPLAY_OTHER to "Other",

    // Personalized advice cards (frontend)
    StringKey.ADVICE_PERSONALIZED_INSIGHTS to "Personalized Insights",
    StringKey.ADVICE_BASED_ON_DAYS to "Based on %s days of data",
    StringKey.ADVICE_ANALYZING_PATTERNS to "Analyzing your spending patterns...",
    StringKey.ADVICE_NO_ADVICE_YET to "No personalized advice available yet.",
    StringKey.ADVICE_KEEP_TRACKING to "Keep tracking your spending to receive tailored insights!",
    StringKey.ADVICE_DISMISS to "Dismiss",

    // Budget categories
    StringKey.BUDGET_CAT_SALARY to "Salary (Net)",
    StringKey.BUDGET_CAT_TIPS to "Tips/Bonuses",
    StringKey.BUDGET_CAT_SIDE_HUSTLES to "Side Hustles",
    StringKey.BUDGET_CAT_FINANCIAL_AID to "Financial Aid",
    StringKey.BUDGET_CAT_INVESTMENTS to "Investments",
    StringKey.BUDGET_CAT_OTHER_INCOME to "Other Income",
    StringKey.BUDGET_CAT_RENT_MORTGAGE to "Rent/Mortgage",
    StringKey.BUDGET_CAT_GROCERIES to "Groceries",
    StringKey.BUDGET_CAT_TRANSPORTATION to "Transportation",
    StringKey.BUDGET_CAT_UTILITIES to "Utilities",
    StringKey.BUDGET_CAT_CLOTHING to "Clothing",
    StringKey.BUDGET_CAT_PHARMACY_MEDICAL to "Pharmacy/Medical",
    StringKey.BUDGET_CAT_SUBSCRIPTIONS to "Subscriptions",
    StringKey.BUDGET_CAT_SPORTS_FITNESS to "Sports/Fitness",
    StringKey.BUDGET_CAT_VACATION to "Vacation",
    StringKey.BUDGET_CAT_GIFTS to "Gifts",
    StringKey.BUDGET_CAT_SCHOOL_EDUCATION to "School/Education",
    StringKey.BUDGET_CAT_FOOD_DINING_OUT to "Food/Dining Out",
    StringKey.BUDGET_CAT_ENTERTAINMENT to "Entertainment",
    StringKey.BUDGET_CAT_INSURANCE to "Insurance",
    StringKey.BUDGET_CAT_PERSONAL_CARE to "Personal Care",
    StringKey.BUDGET_CAT_MISCELLANEOUS to "Miscellaneous",

    // Account staleness / relink
    StringKey.ACCOUNT_RECONNECT_NEEDED to "Reconnect needed",
    StringKey.ACCOUNT_NO_NEW_DATA_DAYS to "No new data %s days",
    StringKey.ACCOUNT_RECONNECT_ACTION to "Reconnect",
    StringKey.RELINK_FALLBACK_TITLE to "Couldn't update this connection",
    StringKey.RELINK_FALLBACK_BODY to "Some banks require a new link to be established. Want to proceed?",
    StringKey.RELINK_FALLBACK_PROCEED to "Yes, proceed",
    StringKey.RELINK_FALLBACK_CANCEL to "Cancel",
    StringKey.CONNECT_FAILED_TITLE to "Couldn't connect",
    StringKey.CONNECT_FAILED_BODY to "The institution may be temporarily unavailable, or this bank's connection may be currently broken on Plaid's side (especially common with Canadian banks). You can try again later, pick a different bank, or check status.plaid.com for ongoing issues.",
    StringKey.CONNECT_FAILED_DISMISS to "Got it",

    // Account custom naming
    StringKey.ACCOUNT_RENAME to "Rename",
    StringKey.ACCOUNT_TYPE_CHEQUING to "Chequing",
    StringKey.ACCOUNT_TYPE_SAVINGS to "Savings",
    StringKey.ACCOUNT_TYPE_CREDIT_CARD to "Credit card",
    StringKey.ACCOUNT_TYPE_TFSA to "TFSA",
    StringKey.ACCOUNT_TYPE_RRSP to "RRSP",
    StringKey.ACCOUNT_TYPE_FHSA to "FHSA",
    StringKey.ACCOUNT_TYPE_RESP to "RESP",
    StringKey.ACCOUNT_TYPE_RDSP to "RDSP",
    StringKey.ACCOUNT_TYPE_RRIF to "RRIF",
    StringKey.ACCOUNT_TYPE_LIRA to "LIRA",
    StringKey.ACCOUNT_TYPE_BROKERAGE to "Brokerage",
    StringKey.ACCOUNT_TYPE_MORTGAGE to "Mortgage",
    StringKey.ACCOUNT_TYPE_GIC to "GIC",
    StringKey.ACCOUNT_TYPE_HSA to "HSA",
    StringKey.ACCOUNT_TYPE_MONEY_MARKET to "Money market",
    StringKey.ACCOUNT_TYPE_CASH_MANAGEMENT to "Cash management",
    StringKey.ACCOUNT_TYPE_401K to "401(k)",
    StringKey.ACCOUNT_TYPE_IRA to "IRA",
    StringKey.ACCOUNT_TYPE_LINE_OF_CREDIT to "Line of credit",
    StringKey.ACCOUNT_TYPE_AUTO_LOAN to "Auto loan",
    StringKey.ACCOUNT_TYPE_STUDENT_LOAN to "Student loan",
    StringKey.ACCOUNT_TYPE_DEPOSITORY to "Bank account",
    StringKey.ACCOUNT_TYPE_CREDIT to "Credit",
    StringKey.ACCOUNT_TYPE_LOAN to "Loan",
    StringKey.ACCOUNT_TYPE_INVESTMENT to "Investment",
    StringKey.ACCOUNT_TYPE_OTHER to "Other",
    StringKey.ACCOUNT_RENAME_PLACEHOLDER to "Your name for this account",
    StringKey.ACCOUNT_RENAME_HINT to "Leave empty to use the bank's name.",
    StringKey.ACCOUNT_RENAME_DUPLICATE to "You already have an account called '%s'.",
    StringKey.ACCOUNT_RENAME_TOO_LONG to "An account name can be at most %s characters.",
    StringKey.ACCOUNT_RENAME_NOT_FOUND to "This account can't be renamed.",
    StringKey.ACCOUNT_RENAME_FORBIDDEN to "You're not allowed to rename this account.",

    // Activity screen strings
    StringKey.ACTIVITY_NO_ACCOUNTS to "No accounts connected",
    StringKey.ACTIVITY_ACCOUNTS to "Accounts",
    StringKey.ACTIVITY_RECENT_ACTIVITY to "Recent Activity",
    StringKey.ACTIVITY_VIEW_ALL to "View all →",
    StringKey.ACTIVITY_GOAL to "Goal",
    StringKey.ACTIVITY_THIS_MONTH to "This Month",
    StringKey.ACTIVITY_TOP_CATEGORIES to "Top Categories",
    StringKey.ACTIVITY_ACCOUNT to "Account",
    StringKey.ACTIVITY_TIP_SEE_MORE to "See more tips in the Goals page",
    StringKey.ACTIVITY_DEMO_ACCOUNT to "Demo Account",
    StringKey.ACTIVITY_UNKNOWN to "Unknown",
    StringKey.ACTIVITY_ERROR_LOADING to "Error Loading Data",
    StringKey.ACTIVITY_FETCHING to "Fetching from %s...",
    StringKey.ACTIVITY_LAST_UPDATED_JUST_NOW to "Last updated from %s: just now",
    StringKey.ACTIVITY_LAST_UPDATED_ONE_MINUTE to "Last updated from %s: 1 minute ago",
    StringKey.ACTIVITY_LAST_UPDATED_MINUTES to "Last updated from %s: %s minutes ago",
    StringKey.ACTIVITY_LAST_UPDATED_ONE_HOUR to "Last updated from %s: 1 hour ago",
    StringKey.ACTIVITY_LAST_UPDATED_HOURS to "Last updated from %s: %s hours ago",
    StringKey.ACTIVITY_SYNC to "↻ Sync",
    StringKey.ACTIVITY_SYNCING to "⏳ Syncing...",
    StringKey.ACTIVITY_AVAILABLE_BALANCE to "%s • Available Balance",
    StringKey.ACTIVITY_FILTERED_BY to "Filtered by:",
    StringKey.ACTIVITY_EXPENSES to "Expenses",
    StringKey.ACTIVITY_NET to "Net",
    StringKey.ACTIVITY_VERIFY_CATEGORY to "Verify category",

    // Confidence labels
    StringKey.CONFIDENCE_AI to "AI: %s%",
    StringKey.CONFIDENCE_USER to "Me: %s%",

    // Chart visibility states
    StringKey.CHART_VISIBILITY_OPEN to "Open",
    StringKey.CHART_VISIBILITY_CLOSED to "Closed",
    StringKey.CHART_VISIBILITY_HIDDEN to "Hidden",

    // Settings section headers
    StringKey.SETTINGS_SECTION_PROFILE to "Profile",
    StringKey.SETTINGS_SECTION_SAVINGS_GOAL to "Savings Goal",
    StringKey.SETTINGS_SECTION_CHARTS to "Dashboard Charts",
    StringKey.SETTINGS_SECTION_PREFERENCES to "Preferences",
    StringKey.SETTINGS_SECTION_SUPPORT to "Support",
    StringKey.SETTINGS_SECTION_FINANCIAL to "Financial Settings",

    // Auth screen
    StringKey.AUTH_USERNAME to "Username",
    StringKey.AUTH_USERNAME_PLACEHOLDER to "Enter your username",
    StringKey.AUTH_EMAIL_PLACEHOLDER to "you@example.com",
    StringKey.AUTH_PASSWORD_PLACEHOLDER to "Enter your password",
    StringKey.AUTH_OR_SEPARATOR to "or",

    // Budget / Charts screen labels
    StringKey.CHART_HIGHEST_SPENDING to "Highest Spending",
    StringKey.CHART_AVERAGE_PER_CATEGORY to "Average per Category",
    StringKey.CHART_SPENDING_PATTERN to "Spending Pattern",
    StringKey.BUDGET_SECTION_SUMMARY to "Summary",

    // Transaction detail labels
    StringKey.TRANSACTION_LABEL_SUBCATEGORY to "Subcategory",
    StringKey.TRANSACTION_MANUAL_ENTRY to "Manual Entry",
    StringKey.TRANSACTION_CSV_IMPORT to "CSV Import",
    StringKey.TRANSACTION_SAVE_SPLIT_ACTION to "Save Split",
    StringKey.TRANSACTION_NOTE_PLACEHOLDER to "Tap to add a note...",
    StringKey.TRANSACTION_ASSIGN_GOAL to "Assign to Goal",
    StringKey.TRANSACTION_REMOVE_GOAL to "None",
    StringKey.TRANSACTION_CHANGE_CATEGORY_DESC to "Recategorize this transaction",
    StringKey.TRANSACTION_ASSIGN_GOAL_DESC to "Track progress towards a savings goal",
    StringKey.TRANSACTION_SPLIT_DESC to "Divide the amount into multiple categories",
    StringKey.TRANSACTION_DISTRIBUTE_DESC to "Spread across months or move to another date",
    StringKey.TRANSACTION_UNSPLIT_DESC to "Remove the split and restore original",
    StringKey.TRANSACTION_UNDISTRIBUTE_DESC to "Remove the distribution and restore original",
    StringKey.TRANSACTION_EXCLUDE to "Exclude Transaction",
    StringKey.TRANSACTION_EXCLUDE_DESC to "Exclude this amount from income and expense calculations",
    StringKey.TRANSACTION_INCLUDE to "Include Transaction",
    StringKey.TRANSACTION_INCLUDE_DESC to "Include this amount back in income and expense calculations",
    StringKey.TRANSACTION_EXCLUDED_BADGE to "Excluded from calculations",
    StringKey.TRANSACTION_EXCLUDED_SHORT to "Excluded",
    StringKey.TRANSACTION_AUTO_EXCLUDED_SHORT to "Auto-excluded",
    StringKey.TRANSACTION_EXCLUDE_SPLIT_WARNING to "This transaction has an active split or distribution. Excluding it will remove the split/distribution. Continue?",
    StringKey.CATEGORY_APPLY_ALL_TITLE to "Also update similar transactions?",
    StringKey.CATEGORY_APPLY_ALL to "Apply to all similar",
    StringKey.CATEGORY_APPLY_JUST_THIS to "Just this transaction",
    StringKey.CATEGORY_APPLY_ALL_NOTE to "Only applies to transactions not previously recategorized by you",
    StringKey.CATEGORY_CHOOSE to "Choose a category",
    StringKey.CATEGORY_ALL_CATEGORIES to "All Categories",
    StringKey.CATEGORY_ALL_SUBCATEGORIES to "All Subcategories",
    StringKey.CATEGORY_CHOOSE_SUBCATEGORY to "Choose subcategory:",
    StringKey.CATEGORY_ADD_BUTTON to "(+ add)",
    StringKey.CATEGORY_ADD_SUBCATEGORY to "Add subcategory",
    StringKey.CATEGORY_EXPENSE_CATEGORIES to "Expense Categories",
    StringKey.CATEGORY_ADD_PRIMARY_DESC to "Add a new primary category. You can then add subcategories inside it.",
    StringKey.CATEGORY_PREVIEW_TITLE to "Transactions that will be updated",
    StringKey.CATEGORY_PREVIEW_CONFIRM to "Apply to all (%s)",
    StringKey.CATEGORY_PREVIEW_SELECT_ALL to "Select all",
    StringKey.CATEGORY_PREVIEW_DESELECT_ALL to "Deselect all",
    StringKey.CATEGORY_PREVIEW_EMPTY to "No other matching transactions found",
    StringKey.CATEGORY_PREVIEW_LOADING to "Loading preview\u2026",
    StringKey.CATEGORY_MODIFY to "Modify",
    StringKey.CATEGORY_ADD_PRIMARY to "Add a primary category",
    StringKey.CATEGORY_ADD_SUB_TITLE to "Add a subcategory",
    StringKey.CATEGORY_EDIT_SUB_TITLE to "Edit subcategory",
    StringKey.CATEGORY_UNDER_PRIMARY to "under %s",
    StringKey.CATEGORY_SUB_NAME_PLACEHOLDER to "Subcategory name",
    StringKey.CATEGORY_PRIMARY_NAME_LABEL to "Primary name",
    StringKey.CATEGORY_PRIMARY_NAME_PLACEHOLDER to "Primary name (e.g. Hobbies)",
    StringKey.CATEGORY_FIRST_SUB_PLACEHOLDER to "First subcategory (e.g. Board games)",
    StringKey.CATEGORY_COLOUR to "Colour",
    StringKey.CATEGORY_CHOOSE_ICON to "Choose an icon",
    StringKey.CATEGORY_SUBCATEGORIES_LABEL to "Subcategories",
    StringKey.CATEGORY_FIRST_SUB_ICON_LABEL to "Icon (used for the primary and its first subcategory)",
    StringKey.CATEGORY_DELETE_TITLE to "Delete \"%s\"?",
    StringKey.CATEGORY_DELETE_USAGE_NONE to "No transactions currently use this subcategory.",
    StringKey.CATEGORY_DELETE_USAGE_ONE to "1 transaction currently uses this subcategory.",
    StringKey.CATEGORY_DELETE_USAGE_MANY to "%s transactions currently use this subcategory.",
    StringKey.CATEGORY_DELETE_MOVE_TO to "Move affected transactions to:",
    StringKey.CATEGORY_DELETE_REMOVE_OVERRIDE to "Remove override \u2014 let AI re-categorize",
    StringKey.CATEGORY_DELETE_AFFECTED_HEADER to "Affected transactions:",
    StringKey.CATEGORY_DELETE_AFFECTED_HEADER_COUNT to "Affected transactions (showing %s of %s):",
    StringKey.CATEGORY_DELETE_CASCADE_NOTE to "This is the last subcategory of \"%s\". Deleting it will also remove the primary category.",

    // Contextual tour UI
    StringKey.TOUR_OPEN_TOOLTIP to "Show a quick tour of this screen",
    StringKey.TOUR_BACK to "Back",
    StringKey.TOUR_NEXT to "Next",
    StringKey.TOUR_DONE to "Done",
    StringKey.TOUR_CLOSE to "Close",
    StringKey.TOUR_STEP_OF to "%s / %s",
    // Transactions-screen tour
    StringKey.TOUR_TXN_NAV_TITLE to "Navigate the app",
    StringKey.TOUR_TXN_NAV_BODY to "You're on Activity. Jump to Dashboard, Goals, or Budget from here anytime.",
    StringKey.TOUR_TXN_SYNC_TITLE to "Sync transactions",
    StringKey.TOUR_TXN_SYNC_BODY to "Pull the latest transactions from your bank.",
    StringKey.TOUR_TXN_ACCOUNTS_TITLE to "Accounts and Contexts",
    StringKey.TOUR_TXN_ACCOUNTS_BODY to "Tap an account to scope the list to its transactions. Contexts (Pro) let you tag and group transactions across accounts.",
    StringKey.TOUR_TXN_ROW_TITLE to "Transaction details",
    StringKey.TOUR_TXN_ROW_BODY to "Tap any row to see details, change the category, split it, or assign it to a savings goal.",
    StringKey.TOUR_TXN_MULTISELECT_TITLE to "Multi-select for batch actions",
    StringKey.TOUR_TXN_MULTISELECT_BODY to "Tap the icon to multi-select. Select a few and a batch actions bar appears \u2014 exclude, re-categorize, or assign to a goal all at once.",
    StringKey.TOUR_TXN_AI_TITLE to "AI categorization",
    StringKey.TOUR_TXN_AI_BODY to "We auto-categorize with AI. Tap the \u2713 to confirm it's right; that trains the system for future transactions.",
    StringKey.TOUR_TXN_MODIFY_TITLE to "Customize categories",
    StringKey.TOUR_TXN_MODIFY_BODY to "Tap Modify on the category picker to recolour any category, and to add or rename your own.",
    // Dashboard-screen tour
    StringKey.TOUR_DASH_SAVINGS_RATE_TITLE to "Savings rate",
    StringKey.TOUR_DASH_SAVINGS_RATE_BODY to "Your savings rate for the selected month, or a yearly average. Click a monthly bar to focus on that one.",
    StringKey.TOUR_DASH_MONTHLY_BARS_TITLE to "Month-by-month overview",
    StringKey.TOUR_DASH_MONTHLY_BARS_BODY to "Each month at a glance — income, expenses, and what's left over. Hover or click any bar for details.",
    StringKey.TOUR_DASH_ACCOUNTS_TITLE to "Filter by account",
    StringKey.TOUR_DASH_ACCOUNTS_BODY to "Filter every chart by account. Uncheck one to exclude it from the numbers.",
    StringKey.TOUR_DASH_CHART_TYPE_TITLE to "Pick a chart",
    StringKey.TOUR_DASH_CHART_TYPE_BODY to "Switch between Savings trend, Expense pie, Bar chart, Timeline, and Year comparison.",
    StringKey.TOUR_DASH_ACTIVE_CHART_TITLE to "Active chart",
    StringKey.TOUR_DASH_ACTIVE_CHART_BODY to "Your selected chart lives here. Pick a different chart type from the sidebar to change what's shown.",
    StringKey.TOUR_DASH_GRID_CANVAS_TITLE to "Move and resize your widgets",
    StringKey.TOUR_DASH_GRID_CANVAS_BODY to "Drag any widget by its title bar to move it. Drag an edge or corner to resize. Widgets that need to move out of the way will. Click × to remove a widget — it returns to the palette.",
    StringKey.TOUR_DASH_CUSTOMIZE_TITLE to "The Customize toolbar",
    StringKey.TOUR_DASH_CUSTOMIZE_BODY to "Click here to open the toolbar. From it you can add or remove widgets, save the current arrangement as a named layout, and lock a layout to prevent accidental edits.",
    StringKey.TOUR_DASH_LAYOUTS_SAVE_TITLE to "Save your own layouts",
    StringKey.TOUR_DASH_LAYOUTS_SAVE_BODY to "Save the current widget arrangement as a named layout. Pro users can save several — keep one for the month-end review, another for daily checks, and switch between them in one click.",
    StringKey.TOUR_DASH_LAYOUTS_SWITCH_TITLE to "Switch layouts in one click",
    StringKey.TOUR_DASH_LAYOUTS_SWITCH_BODY to "Pinned layouts show up here as chips. Click one to swap the whole dashboard arrangement instantly. Great for jumping from a quick KPI check to a deep month-end review.",
    StringKey.TOUR_DASH_LAYOUT_LOCK_TITLE to "Lock a layout",
    StringKey.TOUR_DASH_LAYOUT_LOCK_BODY to "Click the lock icon to freeze a layout — widgets stop being draggable or resizable until you unlock it. Useful once you have an arrangement you don't want to accidentally jostle.",
    StringKey.TOUR_DASH_SAVINGS_TREND_TITLE to "Your savings rate over time",
    StringKey.TOUR_DASH_SAVINGS_TREND_BODY to "Each dot is a month's effective savings rate. The dashed line is your target — watch the trend, see when you're pulling ahead or falling behind.",
    StringKey.TOUR_DASH_EXPENSE_TIMELINE_TITLE to "Monthly spending at a glance",
    StringKey.TOUR_DASH_EXPENSE_TIMELINE_BODY to "Your total expenses each month, traced over the rolling 12 months. Spikes call out the months something unusual happened — vacation, a big bill, a one-off purchase.",

    // Allocation Studio tour
    StringKey.TOUR_STUDIO_HEADER_TITLE to "The Allocation Studio",
    StringKey.TOUR_STUDIO_HEADER_BODY to "This is where you plan a single goal in detail. The title shows the goal name and target date — change the date right here. ✎ Edit Profile re-runs the short onboarding for the assumptions that drive every projection.",
    StringKey.TOUR_STUDIO_PROGRESS_TITLE to "Progress toward your target",
    StringKey.TOUR_STUDIO_PROGRESS_BODY to "How close you are to the goal amount today. Updates as you move the sliders below or as new contributions land in the linked account.",
    StringKey.TOUR_STUDIO_STRIP_TITLE to "Compare account types",
    StringKey.TOUR_STUDIO_STRIP_BODY to "Each card is a different account type (TFSA, RRSP, FHSA, non-registered, etc.) projected out to your target date. Click a card to pin that vehicle — the chart and savings rate below recompute against it.",
    StringKey.TOUR_STUDIO_FORM_TITLE to "Tune the assumptions",
    StringKey.TOUR_STUDIO_FORM_BODY to "Sliders for the target amount, how long you'll save, your monthly contribution, and the return rate. Every change re-projects the chart and updates the vehicle cards above. Nothing is saved until you click Save below.",
    StringKey.TOUR_STUDIO_CHART_TITLE to "Projected growth",
    StringKey.TOUR_STUDIO_CHART_BODY to "Your balance over time under the pinned vehicle's assumptions — base contributions, growth from returns, and the additional savings room estimated over time based on your current info.",
    StringKey.TOUR_STUDIO_SAVINGS_RATE_TITLE to "Savings rate impact",
    StringKey.TOUR_STUDIO_SAVINGS_RATE_BODY to "How this goal's monthly contribution affects your overall savings rate. The bar splits out-of-pocket contributions from the lift you get when registered accounts (RRSP, FHSA) reduce your tax bill — together they're your effective savings rate. A warning appears if a slider pushes the rate above what your income supports.",
    StringKey.TOUR_STUDIO_FOOTER_TITLE to "Save or discard",
    StringKey.TOUR_STUDIO_FOOTER_BODY to "Save commits the new assumptions to the goal so the dashboard picks them up. Cancel discards every change since you opened the Studio.",

    // Debt Paydown Studio tour
    StringKey.TOUR_DEBT_STUDIO_HEADER_TITLE to "The Debt Paydown Studio",
    StringKey.TOUR_DEBT_STUDIO_HEADER_BODY to "This is the planning surface for a debt-payoff goal — mortgage, line of credit, student loan, credit card, anything you owe. The goal name and balance shown sync with whatever's set up on the Goals page.",
    StringKey.TOUR_DEBT_STUDIO_GOAL_TITLE to "Loan terms",
    StringKey.TOUR_DEBT_STUDIO_GOAL_BODY to "The four numbers that define the loan: name, current balance, interest rate, and remaining term in months. Edit any of them — the projections recompute instantly. Priority controls how aggressively the dashboard suggests reallocating money toward this goal.",
    StringKey.TOUR_DEBT_STUDIO_STRIP_TITLE to "Compare two plans side by side",
    StringKey.TOUR_DEBT_STUDIO_STRIP_BODY to "Plan A vs Plan B side by side: payoff date, total interest, total cost. The green delta tells you how much sooner you'd be debt-free and how much interest you'd save vs the other plan. Clicking a card (or its checkbox) commits that plan as the goal's strategy — there's no separate save step.",
    StringKey.TOUR_DEBT_STUDIO_LEVERS_TITLE to "The two plans' levers",
    StringKey.TOUR_DEBT_STUDIO_LEVERS_BODY to "Adjust each plan independently: payment frequency (monthly, semi-monthly, accelerated bi-weekly), payment amount, and any one-time lump sum. Accelerated bi-weekly is the easiest under-the-radar way to finish years sooner — try it.",
    StringKey.TOUR_DEBT_STUDIO_CHART_TITLE to "Watch the balance shrink",
    StringKey.TOUR_DEBT_STUDIO_CHART_BODY to "Your remaining balance over time under each plan vs the current schedule. The lines that converge fastest are the plans that save you the most interest — the gap between them at year 5 or 10 is real dollars in your pocket.",
    StringKey.TOUR_DEBT_STUDIO_FOOTER_TITLE to "How saving works here",
    StringKey.TOUR_DEBT_STUDIO_FOOTER_BODY to "In edit mode, only a Delete button lives here — your goal data auto-saves as you tab out of each field, and the plan you committed up top stays the goal's strategy. In create mode, this area shows Save / Cancel buttons instead, which commit (or discard) the new goal and its selected plan together.",
    StringKey.TOUR_DASH_RECOMMENDATION_TITLE to "Savings suggestions",
    StringKey.TOUR_DASH_RECOMMENDATION_BODY to "Your monthly room to save, plus suggested goals like an emergency fund or vacation.",
    StringKey.TOUR_DASH_GRID_HINT_TITLE to "Prefer a custom layout?",
    StringKey.TOUR_DASH_GRID_HINT_BODY to "Pro users can switch to Grid mode — drag, resize, add or remove widgets to build your own dashboard. Enable it in Settings \u2192 Dashboard mode.",

    StringKey.TOUR_GOALS_ADD_TITLE to "Create a goal",
    StringKey.TOUR_GOALS_ADD_BODY to "Click here to set a savings goal — name it, set a target amount and a deadline, and we'll track your progress.",
    StringKey.TOUR_GOALS_CARD_TITLE to "Your goal at a glance",
    StringKey.TOUR_GOALS_CARD_BODY to "Each card shows a goal's progress toward its target, the deadline, and how much you still need to save. Click a card to open the Allocation Studio — there's a dedicated 'i' tour inside that walks through the projection tools.",
    StringKey.TOUR_GOALS_INSIGHTS_TITLE to "Personalized insights",
    StringKey.TOUR_GOALS_INSIGHTS_BODY to "AI-generated suggestions tailored to your spending and goals. They refresh as your data changes.",
    StringKey.TOUR_GOALS_INSIGHTS_BODY_WIZ3R to "Personalized insights from Wiz 3R, organized as Reveal · Reason · Realize. Reveal surfaces a pattern in your data, Reason explains the math behind it, and Realize hands the next step back to you. Click a card to read the full breakdown or ask Wiz 3R a follow-up question.",
    StringKey.TOUR_GOALS_TIPS_SEARCH_TITLE to "Search financial tips",
    StringKey.TOUR_GOALS_TIPS_SEARCH_BODY to "Browse our library of advice cards — type a keyword or category to find what's relevant to you.",
    StringKey.TOUR_GOALS_PIN_TITLE to "Pin what matters",
    StringKey.TOUR_GOALS_PIN_BODY to "Tap the pin to keep a tip at the top of the page, handy for whenever you need a reminder.",

    StringKey.TOUR_BUDGET_YEAR_TITLE to "Pick the year",
    StringKey.TOUR_BUDGET_YEAR_BODY to "Plan your budget for any year — past, current, or upcoming. Switch here anytime.",
    StringKey.TOUR_BUDGET_INCOME_TITLE to "Income",
    StringKey.TOUR_BUDGET_INCOME_BODY to "Your expected income for each month, broken down by category. Totals update as you type.",
    StringKey.TOUR_BUDGET_EXPENSE_TITLE to "Expenses",
    StringKey.TOUR_BUDGET_EXPENSE_BODY to "Your planned spending, organized by category. Click a row to expand its subcategories for finer control.",
    StringKey.TOUR_BUDGET_CELL_TITLE to "A budget cell",
    StringKey.TOUR_BUDGET_CELL_BODY to "Inline edit + Alt+\u2192 to copy quickly to the next month. A big time-saver when a number repeats.",
    StringKey.TOUR_BUDGET_COPY_YEAR_TITLE to "Start from last year",
    StringKey.TOUR_BUDGET_COPY_YEAR_BODY to "One click duplicates last year's budget into the current year — then tweak only what changed.",
    StringKey.TOUR_BUDGET_NET_TITLE to "Net savings & rate",
    StringKey.TOUR_BUDGET_NET_BODY to "Income minus expenses, monthly and yearly. See immediately how your budget choices affect your savings rate.",

    // AI categorization
    StringKey.AI_CATEGORIZING to "Categorizing with AI...",
    StringKey.AI_PARSE_AND_CATEGORIZE to "Parse & Categorize",
    StringKey.CATEGORY_ASSIGNING to "Assigning...",

    // Goals / Recommendations
    StringKey.GOALS_PERCENT_FUNDED to "% funded",
    StringKey.GOALS_REDUCE_BY to "Reduce by",
    StringKey.GOALS_INTEREST_COMPOUND to "Compound",
    StringKey.GOALS_INTEREST_SIMPLE to "Simple",
    StringKey.GOALS_SPLIT_ACROSS to "Split Across Goals:",
    StringKey.GOALS_DELETE_CONFIRM to "Are you sure you want to delete \"%s\"? This action cannot be undone.",

    // Chart insight titles
    StringKey.CHART_TOTAL_EXPENSES to "Total Expenses",

    // Settings — linked banks / unlink
    StringKey.SETTINGS_SAVINGS_TARGET_HELP to "Set your monthly savings goal percentage. The default 20% comes from the 50/30/20 budgeting rule.",
    StringKey.SETTINGS_CHARTS_REORDER_HELP to "Drag to reorder charts. Control the default display state for each chart on the Dashboard.",
    StringKey.SETTINGS_LINKED_BANKS to "Linked Banks",
    StringKey.SETTINGS_LINKED_BANKS_ERROR to "Could not load linked banks. Please try again later.",
    StringKey.SETTINGS_NO_BANKS_LINKED to "No banks linked yet.",
    StringKey.SETTINGS_ACCOUNTS_COUNT to "account(s)",
    StringKey.SETTINGS_UNLINK to "Unlink",
    StringKey.SETTINGS_UNLINK_CONFIRM_TITLE to "Unlink %s?",
    StringKey.SETTINGS_UNLINK_WARNING to "Unlinking removes this bank's accounts from your dashboard. If you re-link the same bank later, your transaction history will be restored automatically.",
    StringKey.SETTINGS_BIOMETRIC_UNLOCK to "Unlock with %s",
    StringKey.SETTINGS_RATE_APP to "Rate App",
    StringKey.SETTINGS_RATE_APP_DESC to "Love wiz\u2261r? Leave a review",
    StringKey.SETTINGS_BIOMETRIC_TITLE to "Biometric Unlock",
    StringKey.SETTINGS_PRIVACY_DESC to "How we collect, use, and protect your data",
    StringKey.SETTINGS_SECURITY_DESC to "How we protect your data and accounts",
    StringKey.SETTINGS_GUIDE_DESC to "Step-by-step guide to getting the most out of the app",
    StringKey.SETTINGS_UNLINK_ACCOUNTS_REMOVED to "Accounts that will be removed:",
    StringKey.SETTINGS_ABOUT_APP to "About wiz\u2261r",

    // About page
    StringKey.ABOUT_TITLE to "About wiz\u2261r",
    StringKey.ABOUT_MISSION_TITLE to "Our Mission",
    StringKey.ABOUT_MISSION to "Make personal finance so simple that anyone can master it.",
    StringKey.ABOUT_PHILOSOPHY_TITLE to "The One Number Philosophy",
    StringKey.ABOUT_PHILOSOPHY_INTRO to "At the heart of wiz\u2261r is a simple idea: your savings rate is the only number that matters.",
    StringKey.ABOUT_SAVINGS_RATE_FORMULA to "Your savings rate = Money transferred to goals \u00F7 Income",
    StringKey.ABOUT_SAVINGS_RATE_EXPLANATION to "If it's not allocated to a goal, it will be spent. Simple as that.<br><strong>Set goals. Transfer towards them.</strong><br>When you focus on improving just this one number, everything else\u2014budgeting, spending decisions, long-term planning\u2014becomes clearer and simpler.",
    StringKey.ABOUT_QUOTE to "Focus on one number.\nWatch your wealth grow.",
    StringKey.ABOUT_HOW_HELPS_TITLE to "How wiz\u2261r Helps",
    StringKey.ABOUT_HOW_HELPS_INTRO to "wiz\u2261r automatically connects to your bank accounts and credit cards, calculates your savings rate, and helps you improve it through:",
    StringKey.ABOUT_HELP_VISIBILITY to "See your savings rate front and center, updated in real-time",
    StringKey.ABOUT_HELP_INSIGHTS to "AI-powered categorization shows exactly where your money goes",
    StringKey.ABOUT_HELP_HABITS to "Daily advice cards that help you build better financial habits, one small step at a time",
    StringKey.ABOUT_HELP_GOALS to "Set savings goals and watch your progress as your habits improve",
    StringKey.ABOUT_STORY_TITLE to "Our Story",
    StringKey.ABOUT_STORY_P1 to "wiz\u2261r was born from frustration. After trying dozens of budgeting apps, spreadsheets, and financial tools, we realized they all shared the same problem: they made simple things complicated.",
    StringKey.ABOUT_STORY_P2 to "We asked ourselves: what if we built an app that focused on just one thing\u2014helping people save more? What if we stripped away all the complexity and noise, and created something so simple that anyone could use it?",
    StringKey.ABOUT_STORY_P3 to "The result is wiz\u2261r\u2014a personal finance app that respects your time, protects your privacy, and actually helps you build wealth.",
    StringKey.ABOUT_VALUES_TITLE to "Our Values",
    StringKey.ABOUT_VALUE_SIMPLICITY to "Simplicity First: If it's not essential, we don't add it.",
    StringKey.ABOUT_VALUE_PRIVACY to "Privacy by Design: Your financial data is yours. We never sell it.",
    StringKey.ABOUT_VALUE_PRICING to "Honest Pricing: No hidden fees, no dark patterns, no \"unlimited\" plans with limits.",
    StringKey.ABOUT_VALUE_RESULTS to "Results Over Features: We measure success by whether you actually save more, not by feature checklists.",
    StringKey.ABOUT_CONTACT_TITLE to "Contact Us",
    StringKey.ABOUT_CONTACT_INTRO to "We'd love to hear from you.",

    // Statement Accounts
    StringKey.SETTINGS_STATEMENT_ACCOUNTS to "Statement Accounts",
    StringKey.SETTINGS_NO_STATEMENT_ACCOUNTS to "No statement accounts yet.",
    StringKey.SETTINGS_STATEMENT_ACCOUNTS_ERROR to "Could not load statement accounts.",
    StringKey.SETTINGS_DELETE_STATEMENT_ACCOUNT_TITLE to "Delete Statement Account",
    StringKey.SETTINGS_DELETE_STATEMENT_ACCOUNT_WARNING to "This will permanently delete the account and all its imported transactions. This cannot be undone.",
    StringKey.SETTINGS_DELETE_STATEMENT_ACCOUNT_CONFIRM to "Delete Account",
    StringKey.SETTINGS_STATEMENT_TRANSACTIONS_COUNT to "transactions",

    // Chart names (for settings visibility picker)
    StringKey.CHART_NAME_PREVIOUS_YEAR to "Previous Year Chart",
    StringKey.CHART_NAME_SAVINGS_TREND to "Savings Trend",
    StringKey.CHART_NAME_EXPENSE_TIMELINE to "Expense Timeline",
    StringKey.CHART_NAME_EXPENSE_DISTRIBUTION to "Expense Distribution",
    StringKey.CHART_NAME_CATEGORY_SPENDING to "Category Spending",

    // Subscription detection
    StringKey.SUBSCRIPTION_CHECK_TITLE to "Subscription Check",
    StringKey.SUBSCRIPTION_CHECK_SUBTITLE to "Confirm which recurring charges are subscriptions. Only confirmed ones count toward your total.",
    StringKey.SUBSCRIPTION_NONE_DETECTED to "No recurring subscriptions detected yet.",
    StringKey.SUBSCRIPTION_MONTHLY_TOTAL to "Confirmed monthly total",
    StringKey.SUBSCRIPTION_WEEKLY to "Weekly",
    StringKey.SUBSCRIPTION_BIWEEKLY to "Bi-weekly",
    StringKey.SUBSCRIPTION_MONTHLY to "Monthly",
    StringKey.SUBSCRIPTION_CONFIRM_SELECTION to "Confirm Selection",

    // Paywall
    StringKey.PAYWALL_TITLE to "Your Trial Has Ended",
    StringKey.PAYWALL_BODY to "Subscribe to continue using all wiz≡r features. You can still view your dashboard in read-only mode.",
    StringKey.PAYWALL_TITLE_UPGRADE to "Upgrade to Premium",
    StringKey.PAYWALL_BODY_UPGRADE to "Unlock the full wiz≡r experience — unlimited accounts, insights, and smart budgeting.",
    StringKey.PAYWALL_MAYBE_LATER to "Maybe Later",
    StringKey.PAYWALL_SUBSCRIBE to "Subscribe Now",
    StringKey.PAYWALL_READ_ONLY to "Continue in Read-Only Mode",
    StringKey.READ_ONLY_ACTIONS_LOCKED to "Editing is locked. Subscribe to unlock.",
    StringKey.PAYWALL_RESTORE to "Restore Purchase",
    StringKey.PAYWALL_MONTHLY_LABEL to "Monthly",
    StringKey.PAYWALL_YEARLY_LABEL to "Yearly",
    StringKey.PAYWALL_MONTHLY_PRICE to "$6.99/mo",
    StringKey.PAYWALL_YEARLY_PRICE to "$72/yr",
    StringKey.PAYWALL_BEST_VALUE to "BEST VALUE",
    StringKey.PAYWALL_SAVE_14 to "Save 14% — $6.00/mo",
    StringKey.PAYWALL_SAVE_PREFIX to "Save",
    StringKey.PAYWALL_PER_MONTH_SHORT to "/mo",
    StringKey.PAYWALL_BLOCKED_TOAST to "Subscribe to continue",
    StringKey.PAYWALL_BLOCKED_TOAST_CTA to "Subscribe",
    StringKey.PAYWALL_FREE_TRIAL to "14-day free trial included",
    StringKey.PAYWALL_LOADING_PRODUCTS to "Loading plans...",
    StringKey.PAYWALL_PURCHASE_FAILED to "Purchase failed. Please try again.",
    StringKey.PAYWALL_RESTORE_SUCCESS to "Your subscription has been restored.",
    StringKey.PAYWALL_ALREADY_SUBSCRIBED to "You already have an active subscription.",
    StringKey.PAYWALL_AUTO_RENEW_DISCLOSURE to "Subscriptions auto-renew at the selected price until cancelled. You can cancel anytime in your account settings at least 24 hours before the end of the current period.",
    StringKey.PAYWALL_TERMS_OF_USE to "Terms of Use",
    StringKey.PAYWALL_PRIVACY_POLICY to "Privacy Policy",
    StringKey.SETTINGS_CONTACT_SUPPORT to "Contact Support",
    StringKey.SETTINGS_VIEW_ON_WEB to "View Full Version on Web",
    StringKey.TERMS_OVERVIEW_BODY to "By using wiz≡r, you agree to these Terms of Use. wiz≡r is a personal finance application that helps you track your savings rate using read-only access to your bank accounts via Plaid. We never move your money and never store your bank credentials.",
    StringKey.TERMS_SUBSCRIPTION_BODY to "wiz≡r offers auto-renewable subscriptions. Standard and Pro tiers are billed monthly or yearly through your Apple ID. Subscriptions automatically renew at the selected price unless cancelled at least 24 hours before the end of the current period. Manage or cancel anytime in your Apple ID Subscription settings.",
    StringKey.TERMS_ACCEPTABLE_USE_BODY to "You agree to provide accurate information, use the app only for lawful purposes, not attempt to reverse-engineer or interfere with the Service, and respect the intellectual property rights of wiz≡r and its third-party providers (including Plaid).",
    StringKey.TERMS_LIMITATION_BODY to "wiz≡r provides financial information and insights, not financial, tax, or legal advice. You are solely responsible for your financial decisions. The Service is provided \"as is\" without warranties of any kind. To the maximum extent permitted by law, wiz≡r's total liability is limited to the fees you paid in the 12 months preceding the claim.",
    StringKey.TERMS_CONTACT_BODY to "For questions about these Terms or your account, contact support@wiz3r.com. These Terms are governed by the laws of Ontario, Canada.",
    StringKey.AUTH_TERMS_AGREEMENT to "By creating an account, you agree to our [Terms of Use](https://www.apple.com/legal/internet-services/itunes/dev/stdeula/) and [Privacy Policy](https://wiz3r.com/privacy).",

    // Multi-select
    StringKey.MULTISELECT_COUNT to "%s selected",
    StringKey.MULTISELECT_CHANGE_CATEGORY to "Change Category",
    StringKey.MULTISELECT_ASSIGN_TO_GOAL to "Assign to Goal",
    StringKey.MULTISELECT_EXCLUDE to "Exclude",
    StringKey.MULTISELECT_CLEAR_PROMPT to "Clear selection? (%s items selected)",
    StringKey.MULTISELECT_CLEAR to "Clear",
    StringKey.MULTISELECT_KEEP to "Keep",
    StringKey.MULTISELECT_RECATEGORIZED to "%s transactions recategorized",
    StringKey.MULTISELECT_ASSIGNED_TO_GOAL to "%s transactions assigned to %s",
    StringKey.MULTISELECT_EXCLUDED to "%s transactions excluded",
    StringKey.MULTISELECT_INCLUDE to "Include",
    StringKey.MULTISELECT_INCLUDED to "%s transactions included",
    StringKey.MULTISELECT_EXCLUDE_CONFIRM to "Exclude %s transactions from calculations?",
    StringKey.MULTISELECT_PARTIAL_FAILURE to "%s of %s updated. %s failed.",

    // Multi-select batch actions
    StringKey.BATCH_EXCLUDE_COUNT to "Exclude (%s)",
    StringKey.BATCH_INCLUDE_COUNT to "Include (%s)",

    // Contexts (Pro feature)
    StringKey.CONTEXT_TITLE to "Contexts",
    StringKey.CONTEXT_ALL to "All",
    StringKey.CONTEXT_ACTION_TITLE to "Context",
    StringKey.CONTEXT_ACTION_SUBTITLE to "Assign to a context to track separately",
    StringKey.CONTEXT_ASSIGN_TITLE to "Assign Contexts",
    StringKey.CONTEXT_ASSIGN_SUBTITLE to "Select one or more contexts",
    StringKey.CONTEXT_NAME_PLACEHOLDER to "Context name",
    StringKey.CONTEXT_EDIT_TITLE to "Edit context",
    StringKey.CONTEXT_DELETE_CONFIRM to "Remove '%s'?",
    StringKey.DASHBOARD_LAYOUT_NAME to "Layout name",
    StringKey.DASHBOARD_LAYOUT_DELETE_CONFIRM to "Delete layout '%s'?",
    StringKey.CONTEXT_DELETE_CONFIRM_MESSAGE to "%s transactions will lose this context.",
    StringKey.CONTEXT_SHOWING_BOTH to "Showing transactions in both contexts",
    StringKey.CONTEXT_SHOWING_ALL_N to "Showing transactions in all %s contexts",
    StringKey.CONTEXT_EXPENSES_ONLY to "Expenses only",
    StringKey.CONTEXT_BATCH_ASSIGN to "Assign Context",

    // Manual Account / Statement Import
    StringKey.IMPORT_FROM_STATEMENTS to "Import from bank statements",
    StringKey.IMPORT_RECOMMEND_DIRECT to "We recommend connecting directly to your bank for automatic updates. If your bank isn't supported, you can import CSV or PDF statements.",
    StringKey.IMPORT_CSV_OR_PDF to "Import CSV or PDF statements",
    StringKey.IMPORT_ANALYZING to "Analyzing your statement...",
    StringKey.IMPORT_DETECTED to "We detected:",
    StringKey.IMPORT_IS_CORRECT to "Is this correct?",
    StringKey.IMPORT_LOOKS_GOOD to "Looks good",
    StringKey.IMPORT_DOESNT_LOOK_RIGHT to "This doesn't look right",
    StringKey.IMPORT_STATEMENT to "Import Statement",
    StringKey.IMPORT_MISMATCH_WARNING to "This statement appears to be from %s but this account is %s",
    StringKey.IMPORT_ANYWAY to "Import anyway",
    StringKey.IMPORT_SUCCESS to "%s transactions imported into %s",
    StringKey.IMPORT_DUPLICATES_SKIPPED to "%s duplicates skipped",
    StringKey.IMPORT_INSTITUTION_NAME to "Institution",
    StringKey.IMPORT_ACCOUNT_NAME to "Account Name",
    StringKey.IMPORT_ACCOUNT_TYPE to "Account Type",
    StringKey.IMPORT_TRANSACTIONS_PREVIEW to "Transaction Preview",
    StringKey.IMPORT_TOTAL_COUNT to "%s transactions found",
    StringKey.IMPORT_DROP_FILE to "Drop your statement file here",
    StringKey.IMPORT_SELECT_FILE to "Select File",
    StringKey.IMPORT_SUPPORTED_FORMATS to "Supported: CSV, TXT, PDF",
    StringKey.IMPORT_PRIVACY_NOTE to "Your data is processed securely. See our Privacy Policy for details.",
    StringKey.IMPORT_RETRY to "Try different format",
    StringKey.IMPORT_CANCELLED_MESSAGE to "Import cancelled. If the file was very large or unusual, try a shorter date range or a different format.",
    StringKey.IMPORT_FAILED_GENERIC to "Failed to import: %s",
    StringKey.IMPORT_MISMATCH_UNKNOWN to "We couldn't identify which bank this statement is from — please confirm it actually belongs to %s before continuing.",
    StringKey.IMPORT_PASTE_MANUALLY to "Paste transactions manually",
    StringKey.IMPORT_COLUMN_DATE to "Date",
    StringKey.IMPORT_COLUMN_DESCRIPTION to "Description",
    StringKey.IMPORT_COLUMN_AMOUNT to "Amount",
    StringKey.IMPORT_ACCOUNT_TYPE_CHECKING to "Checking",
    StringKey.IMPORT_ACCOUNT_TYPE_SAVINGS to "Savings",
    StringKey.IMPORT_ACCOUNT_TYPE_CREDIT to "Credit Card",

    // Tooltips
    StringKey.TOOLTIP_GO_HOME to "Go to Home",
    StringKey.TOOLTIP_CHANGE_LANGUAGE to "Click to change language",
    StringKey.TOOLTIP_HOW_IT_WORKS to "How it Works",

    // Time periods (display labels)
    StringKey.TIME_WEEKLY to "Weekly",
    StringKey.TIME_MONTHLY to "Monthly",
    StringKey.TIME_QUARTERLY to "Quarterly",

    // Transaction Notifications
    StringKey.NOTIF_SETTINGS_TITLE to "Transaction Notifications",
    StringKey.NOTIF_SETTINGS_ENABLE to "Enable notifications",
    StringKey.NOTIF_SETTINGS_CHANNEL to "Notify via",
    StringKey.NOTIF_SETTINGS_EMAIL to "Email",
    StringKey.NOTIF_SETTINGS_SMS to "SMS",
    StringKey.NOTIF_SETTINGS_BOTH to "Both",
    StringKey.NOTIF_SETTINGS_PHONE to "Phone number",
    StringKey.NOTIF_SETTINGS_VERIFY to "Verify",
    StringKey.NOTIF_SETTINGS_PHONE_VERIFIED to "Verified",
    StringKey.NOTIF_SETTINGS_SMS_CONSENT to "By enabling SMS, you agree to receive transactional messages from wiz≡r. Standard rates apply. Reply STOP to unsubscribe.",
    StringKey.NOTIF_SETTINGS_SMS_AGREE to "I agree to receive SMS notifications",
    StringKey.NOTIF_SETTINGS_UPGRADE_HINT to "Upgrade to Pro to enable notifications",
    StringKey.NOTIF_SETTINGS_ENTER_CODE to "Enter verification code",
    StringKey.NOTIF_SETTINGS_CODE_SENT to "Code sent to your phone",
    StringKey.NOTIF_TEST_SEND to "Send test notification",
    StringKey.NOTIF_TEST_SENDING to "Sending…",
    StringKey.NOTIF_TEST_SENT to "Sent — check your inbox/phone within seconds.",
    StringKey.NOTIF_TEST_NOTHING_FIRED to "Nothing fired. Check server logs.",
    StringKey.NOTIF_TEST_FAILED to "Failed to fire test notification",
    StringKey.NOTIF_EMAIL_SUBJECT to "wiz≡r — %s new transactions",
    StringKey.NOTIF_EMAIL_GREETING to "Hi %s,",
    StringKey.NOTIF_EMAIL_SUMMARY to "You have %s new transactions (%s total) across %s accounts.",
    StringKey.NOTIF_EMAIL_SUMMARY_SINGLE_ACCOUNT to "You have %s new transactions (%s total).",
    StringKey.NOTIF_EMAIL_CONFIRM_ALL to "Confirm All",
    StringKey.NOTIF_EMAIL_REVIEW to "Review in App",
    StringKey.NOTIF_EMAIL_DISMISS to "Dismiss",
    StringKey.NOTIF_EMAIL_FOOTER to "You're receiving this because you enabled transaction notifications. Manage preferences in Settings.",
    StringKey.NOTIF_SMS_SUMMARY to "Wiz≡r: %s new transactions (%s total). Reply 1=Confirm all 2=Review in app 3=Dismiss 4=See details",
    StringKey.NOTIF_SMS_CONFIRM_DONE to "Done! %s transactions confirmed.",
    StringKey.NOTIF_SMS_REVIEW_LINK to "Open wiz≡r: %s",
    StringKey.NOTIF_SMS_DISMISS_ACK to "Got it. You can review anytime in the app.",
    StringKey.NOTIF_SMS_HELP to "Reply 1=Confirm all 2=Review in app 3=Dismiss 4=See details",
    StringKey.NOTIF_SMS_NO_PENDING to "No pending notification. Check the app.",
    StringKey.NOTIF_SMS_EXPIRED to "This notification has expired. Please check the app for latest transactions.",
    StringKey.NOTIF_SMS_ALREADY_ACTED to "You already %s these transactions.",
    StringKey.NOTIF_SMS_DETAILS_FOOTER to "Reply 1=Confirm all 2=Review in app 3=Dismiss",
    StringKey.NOTIF_ACTION_CONFIRMED_TITLE to "Transactions Confirmed",
    StringKey.NOTIF_ACTION_CONFIRMED_MSG to "%s transactions have been confirmed.",
    StringKey.NOTIF_ACTION_DISMISSED_TITLE to "Notification Dismissed",
    StringKey.NOTIF_ACTION_EXPIRED_TITLE to "Notification Expired",
    StringKey.NOTIF_ACTION_EXPIRED_MSG to "This notification has expired. Please check the app for latest transactions.",
    StringKey.NOTIF_ACTION_ALREADY_ACTED_TITLE to "Already Processed",

    // Subscription status card (Settings)
    StringKey.SETTINGS_PLAN_PRO to "Pro Plan",
    StringKey.SETTINGS_PLAN_STANDARD to "Standard Plan",
    StringKey.SETTINGS_PLAN_TRIAL to "Free Trial",
    StringKey.SETTINGS_PLAN_ACTIVE to "Active",
    StringKey.SETTINGS_PLAN_CANCELED to "Canceled — read-only",
    StringKey.SETTINGS_PLAN_EXPIRED to "Trial ended — read-only",
    StringKey.SETTINGS_PLAN_PAST_DUE to "Payment past due",
    StringKey.SETTINGS_MANAGE_SUBSCRIPTION to "Manage Subscription",
    StringKey.SETTINGS_PLAN_ALL_FEATURES to "All features active",
    StringKey.SETTINGS_PLAN_TRIAL_ENJOY to "Enjoy all features during your trial. Choose a plan before it ends to keep access.",
    StringKey.SETTINGS_PLAN_CHOOSE to "Choose a Plan",
    StringKey.SETTINGS_PLAN_UPGRADE_DESC to "Upgrade to Pro for split, distribute, alerts & more.",
    StringKey.SETTINGS_PLAN_UPGRADE_CTA to "Upgrade to Pro",
    StringKey.SETTINGS_PLAN_THANK_YOU to "Thank you for supporting wiz\u2261r. You have access to all Pro features.",
    StringKey.SETTINGS_DELETE_SOFT_CONFIRM to "Are you sure? We'd love to help if something isn't right.",
    StringKey.SETTINGS_DELETE_YES_DELETE to "Yes, I want to delete",
) + mapOf(
    // Overflow bucket. The main mapOf above sits at the Kotlin compiler's
    // overload-resolution limit; new entries go here via `+ mapOf(...)`.
    StringKey.ACCOUNT_BANK_NOT_RETURNING_DATA to
        "Bank isn't returning new transactions. Try again later, or unlink and re-add this account.",
    StringKey.MERGE_RESTORED_TOAST_ONE to
        "Restored %1\$s transactions from your previous %2\$s connection.",
    StringKey.MERGE_RESTORED_TOAST_MANY to
        "Restored %1\$s transactions from %2\$s previous accounts.",
    StringKey.MERGE_PENDING_CONFIRM_PROMPT to
        "We found %1\$s additional account(s) that may match history from your previous connection. Restore that history into your new accounts?",
    StringKey.INSTITUTION_DOWN_TITLE to "%1\$s is currently down",
    StringKey.INSTITUTION_DOWN_BODY to
        "%1\$s is reporting widespread connection failures right now. Re-linking will most likely fail until they recover. This is on their end, not Wiz3r's.",
    StringKey.INSTITUTION_DEGRADED_TITLE to "%1\$s is having issues",
    StringKey.INSTITUTION_DEGRADED_BODY to
        "%1\$s is reporting elevated connection failures right now. Re-linking may fail or be slow. This is on their end, not Wiz3r's.",
    StringKey.INSTITUTION_TRY_ANYWAY to "Try anyway",
    StringKey.INSTITUTION_WAIT to "Wait and try later",

    // Wiz 3R AI cards — section labels and detail page UI
    StringKey.WIZ3R_SECTION_REVEAL to "REVEAL",
    StringKey.WIZ3R_SECTION_REASON to "REASON",
    StringKey.WIZ3R_SECTION_REALIZE to "REALIZE",
    StringKey.WIZ3R_DETAIL_BACK to "← Back",
    StringKey.WIZ3R_ASK to "Ask Wiz 3R →",
    StringKey.WIZ3R_CARD_SEE_WHY to "See why — and ask →",
    StringKey.WIZ3R_CARD_SEE_WHY_NO_ASK to "See why →",
    StringKey.WIZ3R_ASK_COMING_SOON to "Coming soon",
    StringKey.WIZ3R_LOADING_SOURCE_DATA to "Loading source data…",
    StringKey.WIZ3R_KEY_INPUTS to "KEY INPUTS",
    StringKey.WIZ3R_REASON_YEARLY_PROJECTION to "At this pace, over 12 months",
    StringKey.WIZ3R_REASON_PREVIOUS_BEST to "Previous best month",
    StringKey.WIZ3R_REASON_EXTRA_SAVED to "Extra saved vs the month before",
    StringKey.WIZ3R_REASON_SAVED_SO_FAR to "Saved so far",
    StringKey.WIZ3R_REASON_REMAINING_TO_GOAL to "Remaining to reach the goal",
    StringKey.WIZ3R_CARD_NOT_LOADED to "Card not loaded",
    StringKey.WIZ3R_CARD_NOT_LOADED_BODY to
        "This detail page was opened directly. Return to the dashboard and tap a card section again.",
    StringKey.WIZ3R_BACK_TO_DASHBOARD to "← Back to dashboard",
    // REVEAL builder
    StringKey.WIZ3R_REVEAL_GOAL_TXNS_HEADING to "Goal-tagged transactions this month",
    StringKey.WIZ3R_REVEAL_GOAL_TXNS_SUBHEADING to
        "These are the transfers that count toward your savings rate.",
    StringKey.WIZ3R_REVEAL_GOAL_TXNS_EMPTY to "No goal-tagged transactions this month.",
    StringKey.WIZ3R_REVEAL_THIS_MONTH to "Last full month",
    StringKey.WIZ3R_REVEAL_LAST_MONTH to "Month before",
    StringKey.WIZ3R_REVEAL_VS_LAST_MONTH to "vs last month",
    StringKey.WIZ3R_REVEAL_CATEGORY_HEADING to "Top %1\$s transactions this month",
    StringKey.WIZ3R_REVEAL_CATEGORY_HEADING_GENERIC to "Top transactions this month",
    StringKey.WIZ3R_REVEAL_CATEGORY_SUBHEADING to "Largest first. Showing up to 10.",
    StringKey.WIZ3R_REVEAL_CATEGORY_TOTAL_THIS_MONTH to "Total last full month",
    StringKey.WIZ3R_REVEAL_CATEGORY_SHOWN to "Shown",
    StringKey.WIZ3R_REVEAL_CATEGORY_EMPTY to "No transactions found for this period.",
    StringKey.WIZ3R_REVEAL_GOAL_PROGRESS_HEADING to "Contributions toward \"%1\$s\"",
    StringKey.WIZ3R_REVEAL_GOAL_PROGRESS_HEADING_GENERIC to "Recent contributions",
    StringKey.WIZ3R_REVEAL_GOAL_PROGRESS_SUBHEADING to "Last two months.",
    StringKey.WIZ3R_REVEAL_GOAL_PROGRESS_EMPTY to
        "No tagged contributions yet for this goal.",
    StringKey.WIZ3R_REVEAL_CURRENT_BALANCE to "Current balance",
    StringKey.WIZ3R_REVEAL_TARGET to "Target",
    StringKey.WIZ3R_REVEAL_FUNDED to "funded",
    StringKey.WIZ3R_REVEAL_MERCHANT_HEADING to "Recent transactions for these merchants",
    StringKey.WIZ3R_REVEAL_RECURRING_HEADING to "Recurring transactions",
    StringKey.WIZ3R_REVEAL_RECURRING_SUBHEADING to "Detected based on merchant frequency.",
    StringKey.WIZ3R_REVEAL_NO_TRANSACTIONS to "No transactions found.",
    StringKey.WIZ3R_REVEAL_GENERIC_HEADING to "Source data",
    StringKey.WIZ3R_REVEAL_GENERIC_EMPTY to
        "The underlying figures for this card aren't broken down yet.",
    // REASON builder
    StringKey.WIZ3R_REASON_SAVINGS_RATE_THIS to "Savings rate this month",
    StringKey.WIZ3R_REASON_SAVINGS_RATE_PREV to "Savings rate last month",
    StringKey.WIZ3R_REASON_PERCENT_OF_INCOME to "Percent of income",
    StringKey.WIZ3R_REASON_PERCENT_OF_BUDGET to "Percent of budget used",
    StringKey.WIZ3R_REASON_PLANNED_AMOUNT to "Planned amount",
    StringKey.WIZ3R_REASON_CHANGE_VS_PRIOR to "Change vs prior period",
    StringKey.WIZ3R_REASON_AMOUNT to "Amount",
    StringKey.WIZ3R_REASON_GOAL_FUNDED to "Goal funded",
    StringKey.WIZ3R_REASON_EXPLAINER_SAVINGS to
        "Savings rate = goal-tagged contributions ÷ total income for the month. Both income and contributions exclude split/distributed parents and manually excluded transactions, matching the dashboard's bar chart.",
    StringKey.WIZ3R_REASON_EXPLAINER_CATEGORY to
        "Percent of income = category spend ÷ total income this month. A rising share usually means either the category itself grew or income fell.",
    StringKey.WIZ3R_REASON_EXPLAINER_PROJECTION to
        "The projection shows a 5,000-scenario Monte Carlo across the goal's deadline. The shaded bands are the 25–75% and 10–90% percentile ranges; the line is the median.",
    // REALIZE builder
    StringKey.WIZ3R_REALIZE_REVIEW_TXNS to "Review transactions",
    StringKey.WIZ3R_REALIZE_SEE_CATEGORY to "See this category",
    StringKey.WIZ3R_REALIZE_OPEN_GOAL to "Open this goal",
    StringKey.WIZ3R_REALIZE_CREATE_GOAL to "Create a goal",
    StringKey.WIZ3R_REALIZE_REVIEW_SUBSCRIPTIONS to "Review subscriptions",
    StringKey.WIZ3R_REALIZE_READ_GUIDE to "Read the guide",
    StringKey.WIZ3R_REALIZE_OPEN_GOALS to "Open your goals",
    StringKey.WIZ3R_REALIZE_SET_BUDGET to "Set a budget for this category",
    StringKey.WIZ3R_REALIZE_TAG_MORE_TRANSFERS to "Tag more transfers to this goal",
    StringKey.WIZ3R_REALIZE_INTRO_PERSONAL_BEST to
        "Lock in the momentum — review which goal needs the next push.",
    StringKey.WIZ3R_REALIZE_INTRO_SAVINGS_DROP to
        "Look at what changed and decide if it's intentional.",
    StringKey.WIZ3R_REALIZE_INTRO_CATEGORY to
        "If you want to bring this back down, here are the levers.",
    StringKey.WIZ3R_REALIZE_INTRO_ALMOST_COMPLETE to
        "You're close — a small extra push closes the gap.",
    StringKey.WIZ3R_REALIZE_INTRO_BEHIND to
        "If the deadline still matters, increase the pace or extend the date.",

    // Wiz 3R chat panel
    StringKey.WIZ3R_CHAT_TITLE to "Ask Wiz 3R",
    StringKey.WIZ3R_CHAT_INPUT_PLACEHOLDER to "Ask a follow-up question…",
    StringKey.WIZ3R_CHAT_SEND to "Send",
    StringKey.WIZ3R_CHAT_CLEAR to "Clear conversation",
    StringKey.WIZ3R_CHAT_CLEAR_CONFIRM to "Clear the chat for this card?",
    StringKey.WIZ3R_CHAT_EMPTY_PROMPT_TITLE to "What would you like to explore?",
    StringKey.WIZ3R_CHAT_EMPTY_PROMPT_BODY to
        "Ask Wiz 3R anything about this card or your broader finances. Conversations are private to you.",
    StringKey.WIZ3R_CHAT_EMPTY_SUGGESTION_1 to "Why did my savings rate change?",
    StringKey.WIZ3R_CHAT_EMPTY_SUGGESTION_2 to "How would my tax bracket affect this?",
    StringKey.WIZ3R_CHAT_EMPTY_SUGGESTION_3 to "What would happen if I doubled my contribution?",
    StringKey.WIZ3R_CHAT_THINKING to "Wiz 3R is thinking…",
    StringKey.WIZ3R_CHAT_RATE_LIMITED to
        "You've hit the hourly chat limit (30 messages per hour). Try again in a little while.",
    StringKey.WIZ3R_CHAT_PRO_REQUIRED to "Ask Wiz 3R is available on Pro. Upgrade to chat.",
    StringKey.WIZ3R_CHAT_NETWORK_ERROR to "Couldn't reach Wiz 3R. Check your connection and try again.",
    StringKey.WIZ3R_CHAT_DEMO_SIGNUP to "Ask Wiz 3R is part of a free account. Sign up to chat about your own money.",
    StringKey.WIZ3R_CHAT_CLOSE to "Close chat",

    // 50/30/20 budget buckets
    StringKey.BUDGET_BUCKET_NEEDS to "Need",
    StringKey.BUDGET_BUCKET_WANTS to "Want",
    StringKey.BUDGET_BUCKET_SAVINGS to "Saving",
    StringKey.BUDGET_BUCKET_IGNORE to "Ignore",
    StringKey.BUDGET_BUCKET_UNCLASSIFIED to "Unclassified",
    StringKey.BUDGET_BUCKET_CHIP_TITLE to "Click to change bucket (50/30/20)",
    StringKey.BUDGET_BUCKET_DEFAULT_HINT to "Default — click to override",
    StringKey.BUDGET_BUCKET_OVERRIDE_HINT to "Your override",
    StringKey.BUDGET_BUCKET_MIXED to "Mixed",
    StringKey.BUDGET_BUCKET_MIXED_HINT to
        "Sub-categories default to different buckets. Click to set one bucket for the whole group, or expand to override individual sub-categories.",
    StringKey.BUDGET_BUCKET_RESET to "Reset to default",
    StringKey.BUDGET_BUCKET_FOLLOWS_PRIMARY to
        "These transactions have no sub-category, so they follow the bucket set on the category above.",
    // Needs & Wants Flow widget — edit-buckets modal (Task 7)
    StringKey.BUDGET_EDIT_BUCKETS_CTA to "Adjust needs & wants",
    StringKey.BUDGET_EDIT_BUCKETS_TITLE to "Adjust needs & wants",
    StringKey.BUDGET_EDIT_BUCKETS_SHOW_ALL to "Show all categories",
    // Budget primary↔sub source-of-truth transition modal
    StringKey.BUDGET_SWITCH_TO_SUB_TITLE to "Switch to sub-category budgets?",
    StringKey.BUDGET_SWITCH_TO_SUB_BODY to
        "%1\$s currently has a budget set on the primary row. If you continue, sub-categories will become the source of truth and the primary row will display the sum of subs (no longer editable). The current primary amounts will be removed.",
    StringKey.BUDGET_SWITCH_TO_SUB_CONFIRM to "Switch to sub-budgets",
    StringKey.BUDGET_SWITCH_TO_PRIMARY_TITLE to "Replace sub-category amounts?",
    StringKey.BUDGET_SWITCH_TO_PRIMARY_BODY to
        "%1\$s has budgets set on its sub-categories. If you continue, each month's sub-total moves to the primary row (so no data is lost), your new value overrides for the month you just edited, and the individual sub-category amounts are then cleared.",
    StringKey.BUDGET_SWITCH_TO_PRIMARY_CONFIRM to "Replace sub-amounts",
    StringKey.BUDGET_TRANSITION_CANCEL to "Cancel",
    StringKey.BUDGET_PRIMARY_SUM_LABEL to "Σ sum of sub-categories",
    StringKey.BUDGET_PRIMARY_SUM_HINT to
        "Sub-categories are the source of truth for this row. Edit individual sub-cells or click here to switch back to a primary-level budget.",

    // Goal Allocation Studio — onboarding stepper
    StringKey.STUDIO_PREVIEW_BANNER_TITLE to "Goal Allocation Studio (Preview)",
    StringKey.STUDIO_PREVIEW_BANNER_BODY to "Tell us a bit about your situation so we can show you the math for each registered account when the Studio ships.",
    StringKey.STUDIO_PREVIEW_BANNER_CTA to "Set up your profile",
    StringKey.STUDIO_PREVIEW_BANNER_EDIT_CTA to "Edit profile",
    StringKey.STUDIO_STEPPER_PROGRESS to "%s of 7",
    StringKey.STUDIO_STEPPER_SKIP to "Skip — I'll learn as I go",
    StringKey.STUDIO_STEPPER_BACK to "Back",
    StringKey.STUDIO_STEPPER_DONE_TITLE to "All set",
    StringKey.STUDIO_STEPPER_DONE_BODY to "Your Studio profile is saved. When the Studio ships, it'll use these answers to show you the math.",
    StringKey.STUDIO_STEPPER_DONE_DISMISS to "Done",
    StringKey.STUDIO_WHY_LABEL to "ⓘ Why we ask",
    StringKey.STUDIO_WHY_INCOME to "Your income + province determine your marginal tax rate — the engine that decides which account benefits you most.",
    StringKey.STUDIO_WHY_AGE to "Age narrows time horizons and gates FHSA eligibility (the FHSA expires 15 years after the first contribution).",
    StringKey.STUDIO_WHY_HOME to "Having owned a home in Canada makes you ineligible for the FHSA — so we hide it from comparisons.",
    StringKey.STUDIO_WHY_RRSP_ROOM to "The Studio caps projections at your real RRSP contribution room so we never recommend over-contributing.",
    StringKey.STUDIO_WHY_TFSA_ROOM to "Same as RRSP — knowing your TFSA room avoids unrealistic suggestions.",
    StringKey.STUDIO_WHY_TENURE to "Whether you're new to saving or a veteran changes how the Studio frames things — first principles vs stretching your savings rate.",
    StringKey.STUDIO_WHY_PRIORITIES to "Your current focus area gets a 'matches your priority' nudge in the vehicle comparison — the math still leads, but ties break in your favor.",
    StringKey.STUDIO_HOW_LABEL to "ⓘ How to find it",
    StringKey.STUDIO_HOW_FIND_ROOM to "Log into CRA My Account → RRSP and TFSA → look for the current year's deduction limit (RRSP) or available contribution room (TFSA).",
    StringKey.STUDIO_Q_INCOME_TITLE to "Where do you live, and what's your income?",
    StringKey.STUDIO_Q_INCOME_PROVINCE_LABEL to "Province",
    StringKey.STUDIO_Q_INCOME_MODE_GROSS to "Gross annual income",
    StringKey.STUDIO_Q_INCOME_MODE_PAYCHEQUE to "Average paycheque",
    StringKey.STUDIO_Q_INCOME_MODE_BANK to "Capture from my paycheques",
    StringKey.STUDIO_Q_INCOME_MODE_BANK_COMING_SOON to "Coming soon",
    StringKey.STUDIO_Q_INCOME_GROSS_LABEL to "Annual gross (CAD)",
    StringKey.STUDIO_Q_INCOME_PAYCHEQUE_LABEL to "Net per paycheque (CAD)",
    StringKey.STUDIO_Q_INCOME_FREQUENCY_LABEL to "How often",
    StringKey.STUDIO_Q_INCOME_FREQUENCY_MONTHLY to "Monthly",
    StringKey.STUDIO_Q_INCOME_FREQUENCY_BI_WEEKLY to "Bi-weekly",
    StringKey.STUDIO_Q_INCOME_ESTIMATED_GROSS to "Estimated annual gross: ~%s",
    StringKey.STUDIO_Q_AGE_TITLE to "Which age group are you in?",
    StringKey.STUDIO_Q_AGE_18_29 to "18 – 29",
    StringKey.STUDIO_Q_AGE_30_39 to "30 – 39",
    StringKey.STUDIO_Q_AGE_40_49 to "40 – 49",
    StringKey.STUDIO_Q_AGE_50_59 to "50 – 59",
    StringKey.STUDIO_Q_AGE_60_69 to "60 – 69",
    StringKey.STUDIO_Q_AGE_70_PLUS to "70 +",
    StringKey.STUDIO_Q_HOME_TITLE to "Have you ever owned a home in Canada?",
    StringKey.STUDIO_Q_HOME_NEVER to "No, never",
    StringKey.STUDIO_Q_HOME_BEFORE to "Yes, before",
    StringKey.STUDIO_Q_HOME_DECLINED to "Prefer not to say",
    StringKey.STUDIO_Q_RRSP_ROOM_TITLE to "Do you know your RRSP contribution room?",
    StringKey.STUDIO_Q_TFSA_ROOM_TITLE to "Do you know your TFSA contribution room?",
    StringKey.STUDIO_Q_ROOM_KNOWN to "I know it exactly",
    StringKey.STUDIO_Q_ROOM_ROUGH to "Roughly",
    StringKey.STUDIO_Q_ROOM_UNKNOWN to "Not sure — show me how",
    StringKey.STUDIO_Q_ROOM_AMOUNT_LABEL to "Amount (CAD)",
    StringKey.STUDIO_Q_TENURE_TITLE to "How long have you been saving regularly?",
    StringKey.STUDIO_Q_TENURE_NEVER to "Never",
    StringKey.STUDIO_Q_TENURE_FEW_MONTHS to "A few months",
    StringKey.STUDIO_Q_TENURE_ONE_TO_THREE to "1 – 3 years",
    StringKey.STUDIO_Q_TENURE_FIVE_PLUS to "5+ years",
    StringKey.STUDIO_Q_PRIORITIES_TITLE to "What pulls on you most right now?",
    StringKey.STUDIO_Q_PRIORITIES_HINT to "Select up to 2.",
    StringKey.STUDIO_Q_PRIORITY_EMERGENCY to "Emergency cushion",
    StringKey.STUDIO_Q_PRIORITY_HOME to "Home down payment",
    StringKey.STUDIO_Q_PRIORITY_RETIREMENT to "Retirement",
    StringKey.STUDIO_Q_PRIORITY_EDUCATION to "Kids' education",
    StringKey.STUDIO_Q_PRIORITY_NONE to "Nothing specific",
    StringKey.STUDIO_Q_PRIORITIES_CONTINUE to "Continue",
    StringKey.STUDIO_ROOM_CRA_LINK to "Open CRA My Account →",
    StringKey.STUDIO_ROOM_LEAVE_BLANK_NOTE to "We'll leave this blank for now — when you find your number, edit your profile from the Goals page banner.",
    StringKey.STUDIO_Q_INCOME_PROVINCE_PLACEHOLDER to "Select your province",
    // Studio screen
    StringKey.STUDIO_CUSTOMIZE_ALLOCATION to "Compare options",
    StringKey.STUDIO_HEADER_TITLE to "Goal Allocation Studio",
    StringKey.STUDIO_HEADER_EDIT_PROFILE to "Edit profile",
    StringKey.STUDIO_VEHICLE_TFSA to "TFSA",
    StringKey.STUDIO_VEHICLE_RRSP to "RRSP",
    StringKey.STUDIO_VEHICLE_FHSA to "FHSA",
    StringKey.STUDIO_VEHICLE_RESP to "RESP",
    StringKey.STUDIO_VEHICLE_RDSP to "RDSP",
    StringKey.STUDIO_VEHICLE_NON_REGISTERED to "Non-registered",
    StringKey.STUDIO_VEHICLE_HISA to "HISA",
    StringKey.STUDIO_VEHICLE_HISA_INFLATION_NOTE to "Cash doesn't grow — inflation quietly erodes its value over time.",
    StringKey.STUDIO_ACCOUNT_TYPE_SECTION_TITLE to "Account type selection helper",
    StringKey.GOAL_CATEGORY_LABEL to "Category",
    StringKey.GOAL_CATEGORY_HELPER to "Changing the category updates the fields below instantly.",
    StringKey.GOAL_ACCOUNT_NOT_SET to "— not set —",
    StringKey.GOAL_ACCOUNT_LABEL_HISA to "HISA — high-interest savings",
    StringKey.GOAL_ACCOUNT_LABEL_TFSA to "TFSA",
    StringKey.GOAL_ACCOUNT_LABEL_RRSP to "RRSP",
    StringKey.GOAL_ACCOUNT_LABEL_FHSA to "FHSA (first-home savings)",
    StringKey.GOAL_ACCOUNT_LABEL_RESP to "RESP (with CESG matching)",
    StringKey.GOAL_ACCOUNT_LABEL_RDSP to "RDSP",
    StringKey.GOAL_ACCOUNT_LABEL_NON_REGISTERED to "Non-registered (taxable)",
    StringKey.GOAL_ACCOUNT_LABEL_EMPLOYER_PENSION to "Employer pension / group RRSP",
    StringKey.GOAL_ACCOUNT_LABEL_CHEQUING to "Chequing",
    StringKey.GOAL_ACCOUNT_LABEL_OTHER to "Other",
    StringKey.GOAL_DEBT_DETAILS_TITLE to "Debt details (for precise interest-savings math)",
    StringKey.GOAL_DEBT_DETAILS_RATE to "Interest rate (%)",
    StringKey.GOAL_DEBT_DETAILS_BALANCE to "Remaining balance ($)",
    StringKey.GOAL_DEBT_DETAILS_MONTHS to "Remaining term (months)",
    StringKey.STUDIO_BADGE_BEST_FIT to "Best fit",
    StringKey.STUDIO_BADGE_BACKUP to "Backup",
    StringKey.STUDIO_BADGE_NOT_IDEAL to "Not ideal",
    StringKey.STUDIO_BADGE_INELIGIBLE to "Ineligible",
    StringKey.STUDIO_NET_AT_GOAL to "Net at goal date",
    StringKey.STUDIO_ROOM_REMAINING to "Room remaining: ~%s",
    StringKey.STUDIO_LEVER_CONTRIBUTION to "Monthly contribution",
    StringKey.STUDIO_LEVER_RETURN to "Expected annual return",
    StringKey.STUDIO_LEVER_HORIZON to "Time to goal",
    StringKey.STUDIO_LEVER_HORIZON_SET to "Set as deadline",
    StringKey.STUDIO_LEVER_FUTURE_RATE to "Future marginal rate at withdrawal",
    StringKey.STUDIO_LEVER_RESET to "Reset to defaults",
    StringKey.STUDIO_PIN_VEHICLE to "Pin this vehicle",
    StringKey.STUDIO_PINNED_VEHICLE to "Pinned",
    StringKey.STUDIO_YOUR_PICK to "Your pick",
    StringKey.STUDIO_NO_PROFILE_TITLE to "Set up your Studio profile first",
    StringKey.STUDIO_NO_PROFILE_BODY to "We need your tax context to compute the math. Tap Create Goal to walk through the 7 quick questions.",
    StringKey.STUDIO_NO_VEHICLES_EMPTY_STATE to "This goal category doesn't use a registered account (e.g. debt payoff). The Studio's vehicle comparison doesn't apply here.",
    StringKey.STUDIO_CHART_TITLE to "Growth over time",
    StringKey.STUDIO_CHART_LEGEND_NET_HINT to "Solid = in-account · Dashed = paper net worth",
    StringKey.STUDIO_CHART_INCL_REFUNDS to "incl. refunds",
    StringKey.STUDIO_CHART_NET to "net",
    StringKey.STUDIO_CHART_DASHED_NOTE to "Dashed = balance + refunds reinvested. The drop at goal date is the withdrawal tax (RRSP only).",
    StringKey.STUDIO_ROOM_CHART_TITLE to "Contribution room remaining",
    StringKey.STUDIO_ROOM_CHART_REMAINING to "\$%s left",
    StringKey.DEBT_STUDIO_TITLE to "Debt Paydown Studio",
    StringKey.DEBT_STUDIO_CUSTOMIZE_CTA to "Compare paydown strategies",
    StringKey.DEBT_STUDIO_SCENARIO_CURRENT to "Current plan",
    StringKey.DEBT_STUDIO_SCENARIO_ADJUSTED to "Adjusted plan",
    StringKey.DEBT_STUDIO_LEVER_PAYMENT to "Monthly payment",
    StringKey.DEBT_STUDIO_LEVER_RATE to "Interest rate (what-if refinance)",
    StringKey.DEBT_STUDIO_LEVER_LUMPSUM to "Lump sum / cashdown",
    StringKey.DEBT_STUDIO_STAT_PAYOFF to "Months to payoff",
    StringKey.DEBT_STUDIO_STAT_INTEREST to "Total interest",
    StringKey.DEBT_STUDIO_STAT_TOTAL to "Total cost",
    StringKey.DEBT_STUDIO_CHART_TITLE to "Balance over time",
    StringKey.DEBT_STUDIO_CHART_HINT to "Plan A vs Plan B",
    StringKey.DEBT_STUDIO_NEGATIVE_AMORT_WARNING to "This payment doesn't cover interest — balance would grow, not shrink. Try a higher monthly payment.",
    StringKey.DEBT_STUDIO_PHASE2_NOTE to "Variable-rate projections and mortgage term/amortization split land in Phase 2.",
    StringKey.DEBT_STUDIO_PLAN_A to "Plan A",
    StringKey.DEBT_STUDIO_PLAN_B to "Plan B",
    StringKey.DEBT_STUDIO_FREQ_MONTHLY to "Monthly",
    StringKey.DEBT_STUDIO_FREQ_SEMI_MONTHLY to "Semi-monthly",
    StringKey.DEBT_STUDIO_FREQ_ACCEL_BIWEEKLY to "Accelerated bi-weekly",
    StringKey.DEBT_STUDIO_LEVER_PAYMENT_MONTHLY to "Monthly payment",
    StringKey.DEBT_STUDIO_LEVER_PAYMENT_SEMI_MONTHLY to "Semi-monthly payment",
    StringKey.DEBT_STUDIO_LEVER_PAYMENT_ACCEL_BIWEEKLY to "Accelerated bi-weekly payment",
    StringKey.DEBT_STUDIO_PREPAY_NOTE to "Most amortizing loans cap your extra annual payments (often 10–20% of original principal for mortgages). Check your contract.",
    StringKey.DEBT_STUDIO_DELTA_SAVES to "saves %s",
    StringKey.DEBT_STUDIO_DELTA_FINISHES_SOONER to "finishes %s sooner",
    StringKey.DEBT_STUDIO_APPLY_TO_GOAL to "Apply to goal",
    StringKey.DEBT_STUDIO_APPLIED_CHIP to "✓ Applied",
    StringKey.DEBT_STUDIO_APPLIED_TOAST to "%s saved",
    StringKey.DEBT_STUDIO_UNDO to "Undo",
    StringKey.STUDIO_OTHER_GOALS_NOTE to "Other goals using this vehicle may further reduce your available room.",
    StringKey.STUDIO_SAVINGS_RATE_TITLE to "Share of income going to this goal",
    StringKey.STUDIO_SAVINGS_RATE_OUT_OF_POCKET to "out of pocket",
    StringKey.STUDIO_SAVINGS_RATE_TAX_SHIFTED to "tax-shifted",
    StringKey.STUDIO_SAVINGS_RATE_EXPLAINER to "% of your gross income flowing into this goal each year (capped by contribution room when applicable). The growth chart above shows which vehicle ends up larger — a higher share here doesn't mean a better outcome.",
    StringKey.STUDIO_DISCLAIMER to "Wiz3r shows you the math for the assumptions you've provided. This isn't financial advice.",
    StringKey.STUDIO_UNSAVED_CHIP to "unsaved",
    StringKey.STUDIO_FORM_GOAL_NAME_LABEL to "Goal name",
    StringKey.STUDIO_FORM_CATEGORY_LABEL to "Category",
    StringKey.STUDIO_FORM_TARGET_LABEL to "Target amount",
    StringKey.STUDIO_FORM_DEADLINE_LABEL to "Deadline",
    StringKey.STUDIO_FORM_TARGET_DATE_INLINE to "Target:",
    StringKey.STUDIO_PROGRESS_TITLE to "Progress",
    StringKey.STUDIO_SAVE_ACTION to "✓ Save",
    StringKey.STUDIO_CANCEL_ACTION to "✕ Cancel",
    StringKey.STUDIO_CANCEL_TOOLTIP to "Revert to previously saved values",
    StringKey.STUDIO_DELETE_GOAL to "Delete goal",
    StringKey.GOAL_TYPE_PICKER_TITLE to "What kind of goal?",
    StringKey.GOAL_TYPE_PICKER_SAVINGS to "Savings goal",
    StringKey.GOAL_TYPE_PICKER_DEBT to "Debt paydown",
    StringKey.DEBT_STUDIO_GOAL_NAME_LABEL to "Goal name",
    StringKey.DEBT_STUDIO_GOAL_BALANCE_LABEL to "Starting balance",
    StringKey.DEBT_STUDIO_GOAL_RATE_LABEL to "Interest rate (%)",
    StringKey.DEBT_STUDIO_GOAL_TERM_LABEL to "Original term (months)",
    StringKey.DEBT_STUDIO_PLAN_TOOLTIP to "Your active payment plan",

    // Goal Lab
    StringKey.LAB_TITLE to "Goal Lab",
    StringKey.LAB_NAV_BUTTON to "Lab",
    StringKey.GOAL_FORM_LAB_CTA_LABEL to "Explore in the Goal Lab",
    StringKey.GOAL_FORM_LAB_CTA_BODY to "Compare accounts, timelines and what-if scenarios — without changing your goal.",
    StringKey.LAB_APPLY_TO_GOAL to "Apply to goal",
    StringKey.LAB_APPLY_TITLE to "Apply these changes?",
    StringKey.LAB_APPLY_CONFIRM to "Apply",
    StringKey.LAB_APPLY_NO_CHANGES to "No changes to apply",
    StringKey.LAB_SAVE_AS_GOAL to "Save as goal",
    StringKey.LAB_CLOSE to "Close",
    StringKey.LAB_FIELD_TARGET to "Target",
    StringKey.LAB_FIELD_DEADLINE to "Deadline",
    StringKey.LAB_FIELD_MONTHLY to "Monthly contribution",
    StringKey.LAB_FIELD_ACCOUNT to "Account",

    // Dashboard insight pool — secondary lines in the Recommendation widget.
    StringKey.INSIGHT_BUDGET_OVERAGE_TITLE to "Budget overrun",
    StringKey.INSIGHT_BUDGET_OVERAGE_DETAIL to "%s over plan — %s spent of %s budgeted.",
    StringKey.INSIGHT_MOVER_UP_TITLE to "%s is up %s",
    StringKey.INSIGHT_MOVER_DOWN_TITLE to "%s is down %s",
    StringKey.INSIGHT_MOVER_DETAIL to "%s in %s vs %s on your 6-month average.",
    StringKey.INSIGHT_GOAL_BEHIND_TITLE to "%s is falling behind",
    StringKey.INSIGHT_GOAL_BEHIND_DETAIL to "Needs %s a month to land on time — %s set aside so far.",
    StringKey.INSIGHT_YOY_UP_TITLE to "Spending up %s vs last year",
    StringKey.INSIGHT_YOY_DOWN_TITLE to "Spending down %s vs last year",
    StringKey.INSIGHT_YOY_DETAIL to "%s in %s vs %s the same month a year ago.",
    StringKey.INSIGHT_LARGEST_CATEGORY_TITLE to "%s is your biggest expense",
    StringKey.INSIGHT_LARGEST_CATEGORY_DETAIL to "%s — %s of everything you spent in %s.",
    StringKey.INSIGHT_STREAK_TITLE to "%s months on target",
    StringKey.INSIGHT_STREAK_DETAIL to "You've hit your %s savings target %s months in a row.",
    StringKey.INSIGHT_ALSO_WORTH_NOTING to "Also worth noting",
    StringKey.INSIGHT_AVAILABLE_TO_SAVE_LABEL to "available to save in %s",
    StringKey.INSIGHT_OVER_BUDGET_LABEL to "over budget in %s",
    StringKey.INSIGHT_OVER_BUDGET_GUIDANCE to "Reducing expenses comes before funding goals. Trimming a single recurring line item is often enough to turn the month around.",
)
